import { bedtimeNativeFailureRecoveryOfferSchema } from "./bedtime-native-failure-recovery.js";
import { z } from "zod";
import { storyVisualFailureRecoveryOfferSchema } from "./story-visual-failure-recovery.js";
import { bedtimeNativeMediaManifestV1Schema, bedtimeNativeImageModelIdSchema, bedtimeNativeCoverRequestV1Schema, } from "./bedtime-publication.js";
const ulid = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/i, "Expected an opaque ULID");
const sha256 = z.string().regex(/^[a-f0-9]{64}$/i);
const immutableMediaAssetVersionId = z
    .string()
    .min(1)
    .max(1_024)
    .refine((versionId) => versionId === versionId.trim() &&
    !["unversioned", "null", "undefined"].includes(versionId.toLocaleLowerCase()), { message: "Expected an immutable media asset version ID" });
export const workflowKindSchema = z.enum(["INTERVIEW", "PICTURE_BOOK"]);
export const storyModeSchema = z.enum(["TRUE_MEMORY", "INSPIRED_ADVENTURE"]);
export const creativeStoryModeSchema = z.enum([
    "TRUE_MEMORY",
    "INSPIRED_ADVENTURE",
    "ORIGINAL_STORY",
]);
export const storyAgeRangeSchema = z.enum(["2_4", "4_7", "7_10"]);
/**
 * The Bedtime production profile a book was started on (owner decision 1, 2026-11-02). A guided
 * book started after it is a full picture book: twelve pages of words written for the child's age
 * (2–4 or 4–7). A brief without a profile keeps the first profile (eight pages for ages 2–4), so
 * a book never changes length once begun. Set when the brief is first saved; never changed after.
 */
export const bedtimeProductionProfileSchema = z.enum(["BEDTIME_STORY_PIPELINE_V5_PICTURE_BOOK"]);
/** A picture book's pages of words: twelve, filling the hardcover's twelve story spreads. */
export const BEDTIME_PICTURE_BOOK_PAGES = 12;
/**
 * The most words a picture book's page holds: measured with the governed fonts on the classic
 * pages (2026-11-02), 32 fit whatever the line breaks and 34 failed on some.
 */
export const BEDTIME_PICTURE_BOOK_PAGE_WORDS = 32;
/** About how many words a picture book is written to, for each age the guided flow asks. */
export const BEDTIME_PICTURE_BOOK_AGE_WORDS = {
    "2_4": { minimum: 200, maximum: 300 },
    "4_7": { minimum: 250, maximum: 320 },
};
/**
 * Model text that kept its JSON escapes as characters (a backslash-u code for a dash, a backslash
 * before a quotation mark) reads as written: a live story plan showed them to the family
 * (2026-11-02). Only those literal escapes become the characters they stand for; nothing else
 * in the text changes.
 */
export function plainModelText(text) {
    return text
        .replace(/\\u([0-9a-fA-F]{4})/gu, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
        .replace(/\\(["'\\])/gu, "$1");
}
/** True for a brief started on the picture-book profile. */
export function isBedtimePictureBookBrief(brief) {
    return (brief?.productionProfile ===
        bedtimeProductionProfileSchema.enum.BEDTIME_STORY_PIPELINE_V5_PICTURE_BOOK);
}
export const illustrationStyleSchema = z.enum([
    "WATERCOLOR",
    "CUT_PAPER",
    "COLORED_PENCIL",
    "STORYBOOK_GOUACHE",
    "INK_AND_WASH",
    "PAPER_DIORAMA",
    "SOFT_3D",
    "BOLD_SHAPES",
]);
export const storyStageSchema = z.enum([
    "BRIEF",
    "OUTLINE",
    "PAGES",
    "CHARACTERS",
    "ILLUSTRATIONS",
    "NARRATION",
    "PREVIEW",
    "APPROVED",
]);
export const storyGenerationActionSchema = z.enum([
    "FOLLOW_UPS",
    "OUTLINE",
    "DRAFT",
    "REVISE",
    "INTENT_GRAPH",
    "INTENT_CLARIFICATION",
    "VISUAL_DISCOVERY",
]);
export const storyJobTaskSchema = z.enum([
    "INTENT_GRAPH",
    "INTENT_CLARIFICATION",
    "VISUAL_DISCOVERY",
    "FOLLOW_UPS",
    "OUTLINE",
    "DRAFT",
    "REVISE",
    "CHARACTER_SHEET",
    "ILLUSTRATION",
    "UPSCALE",
    "NARRATION",
    "VOICE_CHANGE",
    "SOUNDSCAPE",
    "PICTURE_BOOK_RENDER",
    "V4_CONCEPTS",
    "V4_MANUSCRIPT_CANDIDATES",
    "V4_MANUSCRIPT_CRITIQUE",
    "V4_MANUSCRIPT_FINAL",
    "V4_STORYBOARD",
    "V4_STYLE_BOARD_CANDIDATES",
    "V4_REPRESENTATIVE_PROOFS",
    "V4_FINAL_SPREAD",
    "V4_COVER_CONCEPTS",
    "V4_FINAL_COVER",
    "V4_SEQUENCE_EVALUATION",
    "V4_TARGETED_REPAIR",
    "V4_NARRATION_FINAL",
    "V4_RENDER_SHOWCASE",
    "REPRESENTATIVE_LAYOUT_PROOFS",
    "TARGETED_PUBLICATION_PROOF_REPLACEMENT",
    "NATIVE_PUBLICATION_MEDIA",
    "NATIVE_PUBLICATION_COVER",
    "CANONICAL_PUBLICATION_RENDER",
]);
export const storyJobStateSchema = z.enum([
    "QUEUED",
    "RUNNING",
    "SUCCEEDED",
    "FAILED",
    "NEEDS_ATTENTION",
]);
export const storyCharacterSchema = z
    .object({
    id: ulid,
    name: z.string().trim().min(1).max(100),
    role: z.string().trim().min(1).max(100),
    appearance: z.string().trim().min(1).max(2_000),
    referenceAssetId: ulid.optional(),
    referenceKind: z.enum(["ADULT", "CHILD"]).optional(),
    referenceSource: z.literal("PHOTO_LIBRARY_IMPORT").optional(),
})
    .strict()
    .superRefine((character, context) => {
    if (Boolean(character.referenceAssetId) !== Boolean(character.referenceKind))
        context.addIssue({
            code: "custom",
            path: ["referenceAssetId"],
            message: "A likeness reference requires both an asset and adult/child classification.",
        });
    if (character.referenceSource && !character.referenceAssetId)
        context.addIssue({
            code: "custom",
            path: ["referenceSource"],
            message: "A Photo Library source marker requires an attached likeness reference.",
        });
});
export const storyFollowUpSchema = z
    .object({
    id: ulid,
    question: z.string().trim().min(1).max(500),
    answer: z.string().trim().max(5_000),
})
    .strict();
export const storyCreationExperienceSchema = z
    .object({
    creationPath: z.enum(["GUIDED_MEMORY", "ILLUSTRATE_MY_STORY", "SURPRISE_ME"]),
    language: z.string().trim().min(1).max(80).default("English"),
    occasion: z.string().trim().max(160).default("Bedtime"),
    readingMinutes: z.enum(["QUICK", "CLASSIC", "LONGER"]).default("CLASSIC"),
    tonePreset: z
        .enum(["GENTLE", "FUNNY", "ADVENTUROUS", "DREAMY", "RHYME", "CLASSIC"])
        .default("GENTLE"),
    endingPreset: z
        .enum(["COZY_HOME", "LOVING_REASSURANCE", "GENTLE_SURPRISE", "OPEN_IMAGINATION"])
        .default("COZY_HOME"),
    palette: z.enum(["GOLDEN", "MOONLIT", "GARDEN", "CANDY", "EARTHY"]).default("GOLDEN"),
    illustrationDensity: z.enum(["CALM", "BALANCED", "RICH"]).default("BALANCED"),
    storyWorld: z
        .enum(["FAMILIAR_PLACE", "SECRET_GARDEN", "MOONLIT_VOYAGE", "SKY_KINGDOM", "OWN_IDEA"])
        .default("FAMILIAR_PLACE"),
    storyWorldDetail: z.string().trim().max(500).default(""),
    heroRole: z
        .enum(["CURIOUS_EXPLORER", "KIND_HELPER", "BRAVE_PROTECTOR", "CLEVER_MAKER", "SURPRISE_ME"])
        .default("CURIOUS_EXPLORER"),
    participation: z
        .enum(["READ_ALOUD_REFRAIN", "MAKE_A_SOUND", "FIND_A_DETAIL", "GENTLE_CHOICE", "STORY_ONLY"])
        .default("READ_ALOUD_REFRAIN"),
    likenessStrength: z.enum(["INSPIRED", "RECOGNIZABLE"]).default("RECOGNIZABLE"),
    mustInclude: z.array(z.string().trim().min(1).max(240)).max(12).default([]),
    mustAvoid: z.array(z.string().trim().min(1).max(240)).max(12).default([]),
})
    .strict();
export const storyPronunciationSchema = z
    .object({
    name: z.string().trim().min(1).max(100),
    pronunciation: z.string().trim().min(1).max(240),
})
    .strict();
export const storyFactReviewSchema = z
    .object({
    version: z.literal("STORY_FACT_REVIEW_V1"),
    intendedChildName: z.string().trim().max(100),
    intendedChildRelationship: z.string().trim().max(100),
    ageRange: storyAgeRangeSchema,
    characters: z
        .array(storyCharacterSchema.pick({
        id: true,
        name: true,
        role: true,
        appearance: true,
    }))
        .min(1)
        .max(8),
    storytellerAttribution: z.string().trim().min(1).max(200),
    dedication: z.string().trim().max(1_000),
    mustInclude: z.array(z.string().trim().min(1).max(240)).max(12),
    pronunciations: z.array(storyPronunciationSchema).max(12),
    confirmedAt: z.string().datetime(),
})
    .strict();
export const storyBriefSchema = z
    .object({
    bookId: ulid,
    revision: z.number().int().nonnegative(),
    mode: storyModeSchema,
    ageRange: storyAgeRangeSchema,
    sourceMemory: z.string().trim().max(50_000),
    sourceAssetIds: z.array(ulid).max(10),
    intendedChildName: z.string().trim().max(100),
    intendedChildRelationship: z.string().trim().max(100),
    characters: z.array(storyCharacterSchema).min(1).max(8),
    tone: z.string().trim().min(1).max(200),
    magicLevel: z.enum(["NONE", "GENTLE", "WONDER_FILLED"]),
    theme: z.string().trim().min(1).max(500),
    ending: z.string().trim().min(1).max(1_000),
    dedication: z.string().trim().max(1_000),
    storytellerAttribution: z.string().trim().min(1).max(200),
    illustrationStyle: illustrationStyleSchema,
    experience: storyCreationExperienceSchema.optional(),
    productionProfile: bedtimeProductionProfileSchema.optional(),
    pronunciations: z.array(storyPronunciationSchema).max(12).optional(),
    factReview: storyFactReviewSchema.optional(),
    followUps: z.array(storyFollowUpSchema).max(5),
    likenessDisclosureAccepted: z.boolean(),
    audioProviderDisclosureAccepted: z.boolean().default(false),
    adultConsentAttested: z.boolean(),
    childGuardianAttested: z.boolean(),
    alphaDisclosureAccepted: z.literal(true),
    updatedAt: z.string().datetime(),
})
    .strict()
    .superRefine((brief, context) => {
    const characterIds = brief.characters.map((character) => character.id);
    if (new Set(characterIds).size !== characterIds.length)
        context.addIssue({
            code: "custom",
            path: ["characters"],
            message: "Each named character must have a unique identity.",
        });
    if (!brief.sourceMemory && !brief.sourceAssetIds.length)
        context.addIssue({
            code: "custom",
            path: ["sourceMemory"],
            message: "Type a memory or attach a recording.",
        });
    if (brief.sourceAssetIds.length && !brief.audioProviderDisclosureAccepted)
        context.addIssue({
            code: "custom",
            path: ["audioProviderDisclosureAccepted"],
            message: "Accept the disclosed transcription terms before attaching audio.",
        });
    const pronunciationNames = (brief.pronunciations ?? []).map((entry) => entry.name.toLocaleLowerCase());
    if (new Set(pronunciationNames).size !== pronunciationNames.length)
        context.addIssue({
            code: "custom",
            path: ["pronunciations"],
            message: "Keep only one pronunciation guide for each name.",
        });
    const referenceAssetIds = brief.characters.flatMap((character) => character.referenceAssetId ? [character.referenceAssetId] : []);
    if (new Set(referenceAssetIds).size !== referenceAssetIds.length)
        context.addIssue({
            code: "custom",
            path: ["characters"],
            message: "Each private likeness reference may be assigned to only one named character.",
        });
    if (brief.factReview) {
        const reviewed = brief.factReview;
        const factsStillMatch = reviewed.intendedChildName === brief.intendedChildName &&
            reviewed.intendedChildRelationship === brief.intendedChildRelationship &&
            reviewed.ageRange === brief.ageRange &&
            reviewed.storytellerAttribution === brief.storytellerAttribution &&
            reviewed.dedication === brief.dedication &&
            JSON.stringify(reviewed.characters) ===
                JSON.stringify(brief.characters.map(({ id, name, role, appearance }) => ({
                    id,
                    name,
                    role,
                    appearance,
                }))) &&
            JSON.stringify(reviewed.mustInclude) ===
                JSON.stringify(brief.experience?.mustInclude ?? []) &&
            JSON.stringify(reviewed.pronunciations) === JSON.stringify(brief.pronunciations ?? []);
        if (!factsStillMatch)
            context.addIssue({
                code: "custom",
                path: ["factReview"],
                message: "Review the current names, relationships, age, cast, credit, and dedication again.",
            });
    }
    const references = brief.characters.filter((character) => character.referenceAssetId);
    if (references.filter((character) => character.referenceKind === "ADULT").length > 1)
        context.addIssue({
            code: "custom",
            path: ["characters"],
            message: "Use at most one adult reference.",
        });
    if (references.filter((character) => character.referenceKind === "CHILD").length > 1)
        context.addIssue({
            code: "custom",
            path: ["characters"],
            message: "Use at most one child reference.",
        });
    if (references.length > 0 && !brief.likenessDisclosureAccepted)
        context.addIssue({
            code: "custom",
            path: ["likenessDisclosureAccepted"],
            message: "Reference photos require the illustrated-likeness disclosure.",
        });
    if (references.some((character) => character.referenceKind === "ADULT") &&
        !brief.adultConsentAttested)
        context.addIssue({
            code: "custom",
            path: ["adultConsentAttested"],
            message: "The adult reference requires consent or authorized-representative attestation.",
        });
    if (references.some((character) => character.referenceKind === "CHILD") &&
        !brief.childGuardianAttested)
        context.addIssue({
            code: "custom",
            path: ["childGuardianAttested"],
            message: "The child reference requires parent or legal-guardian attestation.",
        });
});
export const storyAnchorSchema = z
    .object({
    id: ulid,
    text: z.string().trim().min(1).max(2_000),
    sourceStart: z.number().int().nonnegative(),
    sourceEnd: z.number().int().positive(),
})
    .strict()
    .refine((anchor) => anchor.sourceEnd > anchor.sourceStart, {
    path: ["sourceEnd"],
    message: "A source anchor must end after it starts.",
});
export const storyOutlineBeatSchema = z
    .object({
    id: ulid,
    position: z.number().int().nonnegative(),
    title: z.string().trim().min(1).max(200),
    summary: z.string().trim().min(1).max(2_000),
    anchorIds: z.array(ulid).max(20),
    fictional: z.boolean(),
})
    .strict();
export const storyIllustrationSchema = z
    .object({
    id: ulid,
    spreadId: ulid.optional(),
    version: z.number().int().positive(),
    state: z.enum(["QUEUED", "GENERATING", "READY", "REJECTED", "NEEDS_ATTENTION"]),
    originalAssetId: ulid.optional(),
    upscaledAssetId: ulid.optional(),
    selected: z.boolean(),
    modelId: z.string().min(1).max(500),
    seed: z.number().int().nonnegative(),
    promptHashSha256: sha256,
    safetyResult: z.enum(["PENDING", "PASSED", "BLOCKED"]),
    createdAt: z.string().datetime(),
})
    .strict();
export const storySpreadSchema = z
    .object({
    id: ulid,
    position: z.number().int().nonnegative(),
    revision: z.number().int().positive(),
    text: z.string().trim().min(1).max(5_000),
    visualDirection: z.string().trim().min(1).max(2_000),
    anchorIds: z.array(ulid).max(20),
    unsupportedClaims: z.array(z.string().trim().min(1).max(1_000)).max(50),
    fictionalElements: z.array(z.string().trim().min(1).max(1_000)).max(50),
    selectedIllustrationId: ulid.optional(),
    illustrations: z.array(storyIllustrationSchema).max(12),
})
    .strict();
export const storyGenerationProvenanceSchema = z
    .object({
    provider: z.enum(["AMAZON_BEDROCK", "ANTHROPIC"]),
    modelId: z.string().min(1).max(500),
    fallbackUsed: z.boolean(),
    providerRequestId: z.string().min(1).max(500).optional(),
    promptId: z.string().min(1).max(100),
    promptVersion: z.string().min(1).max(100),
    promptHashSha256: sha256,
    schemaId: z.string().min(1).max(100),
    schemaVersion: z.string().min(1).max(100),
    schemaHashSha256: sha256,
    generatedAt: z.string().datetime(),
})
    .strict();
export const storyIntentOriginSchema = z.enum([
    "CUSTOMER_AUTHORED",
    "AI_SUGGESTED",
    "CUSTOMER_EDITED",
]);
export const storyIntentNodeKindSchema = z.enum([
    "FACT",
    "CHARACTER_ARC",
    "EMOTIONAL_MOVEMENT",
    "VISUAL_MOTIF",
    "MUST_INCLUDE",
    "MUST_AVOID",
    "CLOSING_IMAGE",
    "AMBIGUITY",
]);
export const storyIntentNodeSchema = z
    .object({
    id: ulid,
    key: z.string().regex(/^[a-z][a-z0-9-]{0,63}$/u),
    kind: storyIntentNodeKindSchema,
    label: z.string().trim().min(1).max(120),
    detail: z.string().trim().min(1).max(1_000),
    origin: storyIntentOriginSchema,
    confidence: z.number().min(0).max(1),
    protected: z.boolean(),
    status: z.enum(["ACTIVE", "SUGGESTED", "ACCEPTED", "DISMISSED"]),
    sourceQuotes: z.array(z.string().trim().min(1).max(500)).max(6),
    suggestedDetail: z.string().trim().min(1).max(1_000).optional(),
    suggestedConfidence: z.number().min(0).max(1).optional(),
})
    .strict()
    .superRefine((node, context) => {
    if (node.origin === "CUSTOMER_AUTHORED" && node.status !== "ACTIVE")
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "Customer-authored intent remains active until the customer edits it.",
        });
    if (node.origin === "AI_SUGGESTED" && node.status === "ACTIVE")
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "AI suggestions must remain visibly suggested, accepted, or dismissed.",
        });
    if (node.origin === "CUSTOMER_EDITED" && node.status !== "ACCEPTED")
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "A customer edit is an accepted intent decision.",
        });
});
export const storyIntentEdgeSchema = z
    .object({
    fromKey: z.string().regex(/^[a-z][a-z0-9-]{0,63}$/u),
    toKey: z.string().regex(/^[a-z][a-z0-9-]{0,63}$/u),
    relationship: z.enum(["SUPPORTS", "EVOLVES_TO", "PROTECTS", "RESOLVES"]),
})
    .strict();
export const storyIntentClarificationSchema = z
    .object({
    id: ulid,
    question: z.string().trim().min(1).max(300),
    whyItMatters: z.string().trim().min(1).max(300),
    state: z.enum(["OPEN", "ANSWERED", "DISMISSED"]),
    answer: z.string().trim().max(2_000).optional(),
})
    .strict();
export const storyPromiseSchema = z
    .object({
    text: z.string().trim().min(1).max(1_200),
    protagonist: z.string().trim().min(1).max(200),
    emotionalMovement: z.string().trim().min(1).max(500),
    closingImage: z.string().trim().min(1).max(500),
    mustInclude: z.array(z.string().trim().min(1).max(240)).max(12),
    mustAvoid: z.array(z.string().trim().min(1).max(240)).max(12),
    status: z.enum(["DRAFT", "APPROVED"]),
    approvedAt: z.string().datetime().optional(),
    approvedBy: ulid.optional(),
})
    .strict()
    .superRefine((promise, context) => {
    if (promise.status === "APPROVED" && (!promise.approvedAt || !promise.approvedBy))
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "An approved Story Promise requires its approver and approval time.",
        });
    if (promise.status === "DRAFT" &&
        (promise.approvedAt !== undefined || promise.approvedBy !== undefined))
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "A draft Story Promise cannot carry approval metadata.",
        });
});
export const storyIntentProvenanceSchema = z
    .object({
    provider: z.enum(["ANTHROPIC", "LIFESCAPSULE"]),
    modelId: z.string().min(1).max(500),
    providerRequestId: z.string().min(1).max(500),
    promptId: z.literal("bedtime-story-intent"),
    promptVersion: z.string().min(1).max(100),
    promptHashSha256: sha256,
    schemaVersion: z.string().min(1).max(100),
    schemaHashSha256: sha256,
    effort: z.enum(["low", "medium", "high"]),
    cacheStrategy: z.literal("STABLE_INSTRUCTIONS_AND_APPROVED_CONTEXT"),
    generatedAt: z.string().datetime(),
})
    .strict();
export const storyIntentGraphSchema = z
    .object({
    version: z.literal("STORY_INTENT_GRAPH_V1"),
    id: ulid,
    bookId: ulid,
    revision: z.number().int().positive(),
    sourceStoryRevision: z.number().int().nonnegative(),
    sourceUpdatedAt: z.string().datetime(),
    mirror: z
        .object({
        summary: z.string().trim().min(1).max(700),
        customerLanguage: z.array(z.string().trim().min(1).max(300)).max(8),
    })
        .strict(),
    nodes: z.array(storyIntentNodeSchema).min(1).max(48),
    edges: z.array(storyIntentEdgeSchema).max(96),
    clarification: storyIntentClarificationSchema.optional(),
    clarificationProvenance: storyIntentProvenanceSchema.optional(),
    storyPromise: storyPromiseSchema,
    provenance: storyIntentProvenanceSchema,
    updatedAt: z.string().datetime(),
})
    .strict()
    .superRefine((graph, context) => {
    const keys = graph.nodes.map((node) => node.key);
    if (new Set(keys).size !== keys.length)
        context.addIssue({
            code: "custom",
            path: ["nodes"],
            message: "Story Intent node keys must be unique.",
        });
    const known = new Set(keys);
    for (const [index, edge] of graph.edges.entries())
        if (!known.has(edge.fromKey) || !known.has(edge.toKey))
            context.addIssue({
                code: "custom",
                path: ["edges", index],
                message: "Every Story Intent relationship must reference existing nodes.",
            });
});
export const storyVisualPaletteColorSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    hex: z.string().regex(/^#[0-9A-F]{6}$/i),
})
    .strict();
export const storyVisualDirectionContentSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    thesis: z.string().trim().min(1).max(500),
    character: z.string().trim().min(1).max(600),
    world: z.string().trim().min(1).max(600),
    emotion: z.string().trim().min(1).max(600),
    palette: z.array(storyVisualPaletteColorSchema).min(3).max(6),
    texture: z.string().trim().min(1).max(400),
    lighting: z.string().trim().min(1).max(400),
    composition: z.string().trim().min(1).max(600),
    motifs: z.array(z.string().trim().min(1).max(200)).min(1).max(8),
    closingImage: z.string().trim().min(1).max(600),
    preserves: z.array(z.string().trim().min(1).max(240)).min(1).max(12),
    mustAvoid: z.array(z.string().trim().min(1).max(240)).max(12),
})
    .strict();
export const storyVisualDirectionSchema = storyVisualDirectionContentSchema
    .extend({
    id: ulid,
    key: z.string().regex(/^[a-z][a-z0-9-]{0,63}$/u),
    origin: z.enum(["AI_SUGGESTED", "CUSTOMER_EDITED"]),
    confidence: z.number().min(0).max(1),
    status: z.enum(["SUGGESTED", "ACCEPTED", "DISMISSED"]),
    suggestedContent: storyVisualDirectionContentSchema.optional(),
    suggestedConfidence: z.number().min(0).max(1).optional(),
})
    .strict()
    .superRefine((direction, context) => {
    if (direction.origin === "CUSTOMER_EDITED" && direction.status !== "ACCEPTED")
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "A customer-edited visual direction remains accepted until it is undone.",
        });
});
export const storyVisualConstraintsSchema = z
    .object({
    protectedDetails: z.array(z.string().trim().min(1).max(500)).max(24),
    likenessRules: z.array(z.string().trim().min(1).max(500)).max(8),
    mustInclude: z.array(z.string().trim().min(1).max(240)).max(12),
    mustAvoid: z.array(z.string().trim().min(1).max(240)).max(12),
    closingImage: z.string().trim().min(1).max(600),
})
    .strict();
export const storyVisualPromiseSchema = z
    .object({
    text: z.string().trim().min(1).max(1_200),
    directionKey: z.string().regex(/^[a-z][a-z0-9-]{0,63}$/u),
    character: z.string().trim().min(1).max(600),
    world: z.string().trim().min(1).max(600),
    emotionalMovement: z.string().trim().min(1).max(600),
    palette: z.array(storyVisualPaletteColorSchema).min(3).max(6),
    texture: z.string().trim().min(1).max(400),
    lighting: z.string().trim().min(1).max(400),
    composition: z.string().trim().min(1).max(600),
    motifs: z.array(z.string().trim().min(1).max(200)).min(1).max(8),
    closingImage: z.string().trim().min(1).max(600),
    mustInclude: z.array(z.string().trim().min(1).max(240)).max(12),
    mustAvoid: z.array(z.string().trim().min(1).max(240)).max(12),
    status: z.enum(["DRAFT", "APPROVED"]),
    approvedAt: z.string().datetime().optional(),
    approvedBy: ulid.optional(),
})
    .strict()
    .superRefine((promise, context) => {
    if (promise.status === "APPROVED" && (!promise.approvedAt || !promise.approvedBy))
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "An approved Visual Promise requires its approver and approval time.",
        });
    if (promise.status === "DRAFT" &&
        (promise.approvedAt !== undefined || promise.approvedBy !== undefined))
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "A draft Visual Promise cannot carry approval metadata.",
        });
});
export const storyVisualDiscoveryProvenanceSchema = z
    .object({
    provider: z.enum(["ANTHROPIC", "LIFESCAPSULE"]),
    modelId: z.string().min(1).max(500),
    providerRequestId: z.string().min(1).max(500),
    promptId: z.literal("bedtime-visual-discovery"),
    promptVersion: z.string().min(1).max(100),
    promptHashSha256: sha256,
    schemaVersion: z.string().min(1).max(100),
    schemaHashSha256: sha256,
    effort: z.enum(["low", "medium", "high"]),
    cacheStrategy: z.literal("STABLE_INSTRUCTIONS_AND_APPROVED_CONTEXT"),
    generatedAt: z.string().datetime(),
})
    .strict();
export const storyVisualDiscoverySchema = z
    .object({
    version: z.literal("STORY_VISUAL_DISCOVERY_V1"),
    id: ulid,
    bookId: ulid,
    revision: z.number().int().positive(),
    sourceStoryRevision: z.number().int().nonnegative(),
    sourceIntentRevision: z.number().int().positive(),
    directions: z.array(storyVisualDirectionSchema).min(2).max(4),
    constraints: storyVisualConstraintsSchema,
    visualPromise: storyVisualPromiseSchema,
    provenance: storyVisualDiscoveryProvenanceSchema,
    updatedAt: z.string().datetime(),
})
    .strict()
    .superRefine((discovery, context) => {
    const keys = discovery.directions.map((direction) => direction.key);
    if (new Set(keys).size !== keys.length)
        context.addIssue({
            code: "custom",
            path: ["directions"],
            message: "Visual direction keys must be unique.",
        });
    if (!keys.includes(discovery.visualPromise.directionKey))
        context.addIssue({
            code: "custom",
            path: ["visualPromise", "directionKey"],
            message: "The Visual Promise must refer to one available visual direction.",
        });
});
export const storyCharacterBibleSchema = z
    .object({
    revision: z.number().int().positive(),
    summary: z.string().trim().min(1).max(10_000),
    palette: z
        .array(z.string().regex(/^#[0-9A-F]{6}$/i))
        .min(3)
        .max(8),
    seed: z.number().int().nonnegative(),
    characterSheetAssetId: ulid.optional(),
    approved: z.boolean(),
    approvedAt: z.string().datetime().optional(),
})
    .strict();
export const storyRevisionSchema = z
    .object({
    id: ulid,
    bookId: ulid,
    revision: z.number().int().positive(),
    parentRevision: z.number().int().nonnegative(),
    title: z.string().trim().min(1).max(160),
    synopsis: z.string().trim().min(1).max(3_000),
    mode: storyModeSchema,
    ageRange: storyAgeRangeSchema,
    outline: z.array(storyOutlineBeatSchema).min(1).max(16),
    outlineApproved: z.boolean(),
    sourceAnchors: z.array(storyAnchorSchema).max(100),
    characterBible: storyCharacterBibleSchema.optional(),
    spreads: z.array(storySpreadSchema).max(12),
    creativeDisclosure: z.string().trim().max(2_000),
    status: z.enum(["DRAFT", "PENDING_APPROVAL", "APPROVED", "SUPERSEDED"]),
    provenance: storyGenerationProvenanceSchema.optional(),
    createdBy: ulid,
    createdAt: z.string().datetime(),
    approvedBy: ulid.optional(),
    approvedAt: z.string().datetime().optional(),
})
    .strict();
export const storyAuthoringContractSchema = z.enum(["PICTURE_BOOK_V3", "BEDTIME_STORY_V4"]);
export const storyProjectSchema = z
    .object({
    bookId: ulid,
    authoringContract: storyAuthoringContractSchema.optional(),
    stage: storyStageSchema,
    revision: z.number().int().nonnegative(),
    activeRevision: z.number().int().nonnegative(),
    latestRevision: z.number().int().nonnegative(),
    outlineApproved: z.boolean(),
    characterSheetApproved: z.boolean(),
    printable: z.boolean(),
    pendingJobIds: z.array(ulid).max(20),
    remainingAllowance: z
        .object({
        text: z.number().int().nonnegative(),
        illustrations: z.number().int().nonnegative(),
        narrations: z.number().int().nonnegative(),
    })
        .strict(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const generationJobSchema = z
    .object({
    id: ulid,
    bookId: ulid,
    requestedBy: ulid,
    task: storyJobTaskSchema,
    state: storyJobStateSchema,
    expectedRevision: z.number().int().nonnegative(),
    modelId: z.string().min(1).max(500),
    attempt: z.number().int().nonnegative().max(3),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    cacheCreationInputTokens: z.number().int().nonnegative().optional(),
    cacheReadInputTokens: z.number().int().nonnegative().optional(),
    estimatedCostMicros: z.number().int().nonnegative(),
    actualCostMicros: z.number().int().nonnegative().optional(),
    costEvidenceSource: z
        .enum(["PROVIDER_REPORTED_USAGE", "FALLBACK_ESTIMATE", "NO_PROVIDER_COST"])
        .optional(),
    pricingVersion: z.string().min(1).max(100).optional(),
    provider: z.enum(["AWS", "ANTHROPIC", "OPENAI", "ELEVENLABS", "LIFESCAPSULE"]).optional(),
    providerRequestId: z.string().min(1).max(500).optional(),
    providerCleanupState: z
        .enum(["PENDING", "DELETED", "NOT_AVAILABLE", "NEEDS_ATTENTION"])
        .optional(),
    characterCost: z.number().int().nonnegative().optional(),
    failureReason: z.string().max(500).optional(),
    failureDiagnostic: z.string().max(8_000).optional(),
    /** Set when the buyer's "try again" released the book's hold on this failed, settled job. */
    holdReleasedAt: z.string().datetime().optional(),
    holdReleasedByActorId: ulid.optional(),
    holdReleaseReason: z.literal("SETTLED_FAILURE_RELEASED_ON_RETRY").optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const generatedAssetSchema = z
    .object({
    id: ulid,
    bookId: ulid,
    kind: z.enum([
        "CHARACTER_SHEET",
        "ILLUSTRATION",
        "UPSCALED_ILLUSTRATION",
        "NARRATION",
        "SOUNDSCAPE",
        "PICTURE_BOOK_PDF",
        "SHOWCASE_STYLE_BOARD",
        "SHOWCASE_PROOF",
        "SHOWCASE_COMPOSED_PROOF",
        "SHOWCASE_COMPOSED_PROOF_PDF",
        "SHOWCASE_SPREAD",
        "SHOWCASE_COVER",
        "SHOWCASE_ARCHIVAL_PDF",
        "SHOWCASE_INTERIOR_PDF",
        "SHOWCASE_COVER_PDF",
        "SHOWCASE_BROWSER_PREVIEW",
    ]),
    objectKey: z.string().min(1).max(1_024),
    objectVersionId: z.string().min(1).max(1_024),
    checksumSha256: sha256,
    contentType: z.string().min(1).max(200),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    ppi: z.number().int().positive().optional(),
    colorSpace: z.enum(["RGB", "CMYK"]).optional(),
    generatedTypography: z.boolean().optional(),
    enhancedFromAssetId: ulid.optional(),
    superResolutionModel: z.string().min(1).max(200).optional(),
    superResolutionVersion: z.string().min(1).max(100).optional(),
    superResolutionWeightsSha256: sha256.optional(),
    pageCount: z.number().int().positive().optional(),
    safetyResult: z.enum(["PASSED", "BLOCKED"]),
    provider: z.enum(["AWS", "OPENAI", "ELEVENLABS", "LIFESCAPSULE"]).optional(),
    modelId: z.string().min(1).max(500),
    seed: z.number().int().nonnegative().optional(),
    promptHashSha256: sha256.optional(),
    providerRequestId: z.string().min(1).max(500).optional(),
    providerCleanupState: z
        .enum(["PENDING", "DELETED", "NOT_AVAILABLE", "NEEDS_ATTENTION"])
        .optional(),
    characterCost: z.number().int().nonnegative().optional(),
    referenceAssetVersions: z
        .array(z.object({ assetId: ulid, versionId: z.string().min(1).max(1_024) }).strict())
        .max(4),
    createdAt: z.string().datetime(),
})
    .strict();
export const consentReceiptSchema = z
    .object({
    id: ulid,
    actorId: ulid,
    bookId: ulid,
    mediaAssetId: ulid,
    mediaAssetVersionId: immutableMediaAssetVersionId.optional(),
    legacyUnversioned: z.literal(true).optional(),
    characterId: ulid.optional(),
    importGrantId: ulid.optional(),
    subjectKind: z.enum(["ADULT_SELF_OR_AUTHORIZED", "CHILD_GUARDIAN_ATTESTATION"]),
    consentVersion: z.literal("likeness-v1"),
    scope: z.literal("ILLUSTRATED_LIKENESS"),
    revoked: z.boolean(),
    createdAt: z.string().datetime(),
    revokedAt: z.string().datetime().optional(),
})
    .strict()
    .superRefine((receipt, context) => {
    if (!receipt.mediaAssetVersionId && !receipt.legacyUnversioned)
        context.addIssue({
            code: "custom",
            path: ["mediaAssetVersionId"],
            message: "Consent receipts require an immutable media version unless explicitly legacy.",
        });
    if (receipt.mediaAssetVersionId && receipt.legacyUnversioned)
        context.addIssue({
            code: "custom",
            path: ["legacyUnversioned"],
            message: "Version-bound consent receipts cannot be marked legacy-unversioned.",
        });
    if (receipt.revoked && !receipt.revokedAt)
        context.addIssue({
            code: "custom",
            path: ["revokedAt"],
            message: "Revoked consent receipts require a revocation timestamp.",
        });
    if (!receipt.revoked && receipt.revokedAt)
        context.addIssue({
            code: "custom",
            path: ["revokedAt"],
            message: "Active consent receipts cannot carry a revocation timestamp.",
        });
});
export const createStoryCharacterPhotoImportCommandSchema = z
    .object({
    version: z.literal(1),
    photoId: ulid,
    characterId: ulid,
    referenceKind: z.enum(["ADULT", "CHILD"]),
    purpose: z.literal("ILLUSTRATED_LIKENESS"),
    likenessDisclosureAccepted: z.literal(true),
    adultConsentAttested: z.boolean(),
    childGuardianAttested: z.boolean(),
    expectedStoryRevision: z.number().int().nonnegative(),
})
    .strict()
    .superRefine((command, context) => {
    if (command.referenceKind === "ADULT" && !command.adultConsentAttested)
        context.addIssue({
            code: "custom",
            path: ["adultConsentAttested"],
            message: "Adult likeness imports require self, consent, or authorized-use attestation.",
        });
    if (command.referenceKind === "CHILD" && !command.childGuardianAttested)
        context.addIssue({
            code: "custom",
            path: ["childGuardianAttested"],
            message: "Child likeness imports require guardian attestation.",
        });
});
export const revokeStoryCharacterReferenceCommandSchema = z
    .object({
    version: z.literal(1),
    characterId: ulid,
    expectedStoryRevision: z.number().int().nonnegative(),
    reason: z.literal("USER_REMOVED"),
})
    .strict();
export const storyCharacterPhotoImportResultSchema = z
    .object({
    version: z.literal(1),
    mediaAssetId: ulid,
    characterId: ulid,
    referenceKind: z.enum(["ADULT", "CHILD"]),
    consentReceiptId: ulid,
    status: z.enum(["ACTIVE", "REVOKED"]),
    storyRevision: z.number().int().nonnegative(),
    attachedToBrief: z.literal(true),
    createdAt: z.string().datetime(),
    revokedAt: z.string().datetime().optional(),
})
    .strict()
    .superRefine((result, context) => {
    if (result.status === "REVOKED" && !result.revokedAt)
        context.addIssue({
            code: "custom",
            path: ["revokedAt"],
            message: "Revoked character-photo imports require a revocation timestamp.",
        });
    if (result.status === "ACTIVE" && result.revokedAt)
        context.addIssue({
            code: "custom",
            path: ["revokedAt"],
            message: "Active character-photo imports cannot carry a revocation timestamp.",
        });
});
export const revokeStoryCharacterReferenceResultSchema = z
    .object({
    version: z.literal(1),
    mediaAssetId: ulid,
    characterId: ulid,
    status: z.literal("REVOKED"),
    storyRevision: z.number().int().nonnegative(),
    detachedFromBrief: z.boolean(),
    revokedConsentCount: z.number().int().nonnegative(),
    revokedAt: z.string().datetime(),
})
    .strict();
export const narrationAssetSchema = z
    .object({
    id: ulid,
    bookId: ulid,
    storyRevision: z.number().int().positive(),
    spreadId: ulid.optional(),
    source: z.enum(["RECORDED", "SYNTHETIC", "VOICE_CHANGED"]),
    provider: z.enum(["ELEVENLABS", "CUSTOMER_RECORDING", "AWS_POLLY"]).default("AWS_POLLY"),
    voice: z.enum(["Ruth", "Danielle", "Matthew", "Stephen"]).optional(),
    voiceId: z.string().trim().min(1).max(200).optional(),
    voiceName: z.string().trim().min(1).max(200).optional(),
    modelId: z.string().trim().min(1).max(200).optional(),
    assetId: ulid,
    durationSeconds: z.number().nonnegative(),
    alignment: z
        .array(z
        .object({
        text: z.string().min(1).max(200),
        startSeconds: z.number().nonnegative(),
        endSeconds: z.number().nonnegative(),
        spreadId: ulid.optional(),
    })
        .strict())
        .max(5_000)
        .optional(),
    stale: z.boolean(),
    shareId: ulid.optional(),
    createdAt: z.string().datetime(),
})
    .strict();
// Bedtime Story v4 is intentionally a separate contract. Existing v3 records do not
// acquire defaults or new required fields when the showcase feature is enabled.
export const showcaseContractSchema = z.literal("BEDTIME_STORY_V4");
export const showcaseApprovalStageSchema = z.enum([
    "MANUSCRIPT",
    "STORYBOARD_STYLE_BOARD",
    "FINAL_PROOF",
]);
export const showcaseApprovalStateSchema = z.enum(["PENDING", "APPROVED", "REJECTED"]);
export const showcaseApprovalRecordSchema = z
    .object({
    id: ulid,
    bookId: ulid,
    contract: showcaseContractSchema,
    stage: showcaseApprovalStageSchema,
    state: z.literal("APPROVED"),
    sourceRevision: z.number().int().nonnegative().optional(),
    approvedArtifactIds: z.array(ulid).min(1).max(20),
    selectedCoverAssetId: ulid.optional(),
    approvedBy: ulid,
    approvedAt: z.string().datetime(),
})
    .strict();
export const showcaseLayoutArchetypeSchema = z.enum([
    "FULL_BLEED",
    "VIGNETTE",
    "INTEGRATED_NEGATIVE_SPACE",
    "FACING_PAGE",
    "SPOT_ART",
    "QUIET_BEAT",
    "SEQUENTIAL",
]);
export const showcaseShotScaleSchema = z.enum([
    "EXTREME_WIDE",
    "WIDE",
    "MEDIUM",
    "CLOSE_UP",
    "DETAIL",
]);
export const showcasePalettePhaseSchema = z.enum(["DUSK", "MOONRISE", "BLOOM", "REST"]);
const normalizedSafeRegionSchema = z
    .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().positive().max(1),
    height: z.number().positive().max(1),
})
    .strict()
    .refine((region) => region.x + region.width <= 1 && region.y + region.height <= 1, {
    message: "A typography-safe region must remain inside the spread.",
});
export const showcaseTypographyRoleSchema = z.enum([
    "BODY",
    "DIALOGUE",
    "DISPLAY",
    "REFRAIN",
    "COUNT",
    "REACTION",
    "WHISPER",
]);
export const showcaseTypographyTreatmentSchema = z.enum([
    "QUIET_EMBLEM",
    "NIGHT_CONSTELLATION",
    "GARDEN_THRESHOLD",
]);
export const typographyMomentSchema = z
    .object({
    role: showcaseTypographyRoleSchema,
    lines: z.array(z.string().trim().min(1).max(160)).min(1).max(4),
    region: normalizedSafeRegionSchema,
    alignment: z.enum(["LEFT", "CENTER", "RIGHT"]),
    verticalAlignment: z.enum(["TOP", "CENTER", "BOTTOM"]),
    surface: z.enum(["ART", "PAPER", "RAIL"]),
    fontSizePoints: z.number().int().min(29).max(60),
    lineHeight: z.number().min(1).max(1.4),
    weight: z.enum(["REGULAR", "SEMIBOLD"]),
    textColor: z.enum(["INK", "CREAM"]),
    backgroundTone: z.enum(["LIGHT", "DARK"]),
    support: z.enum(["NONE", "SUBTLE_WASH"]),
    /** Designed books only: up to two whole words the storyboard sets in the kit's italic. */
    emphasisWords: z.array(z.string().trim().min(1).max(40)).min(1).max(2).optional(),
    /** Designed books only: one sound or surprise word set in the kit's display lettering. */
    displayWord: z.string().trim().min(1).max(40).optional(),
})
    .strict()
    .superRefine((moment, context) => {
    const displayRole = ["DISPLAY", "REFRAIN", "COUNT", "REACTION"].includes(moment.role);
    const minimumBodySize = 29;
    if (displayRole && moment.fontSizePoints < 36)
        context.addIssue({
            code: "custom",
            path: ["fontSizePoints"],
            message: "Display typography must be at least 36 points.",
        });
    if (!displayRole && moment.fontSizePoints < minimumBodySize)
        context.addIssue({
            code: "custom",
            path: ["fontSizePoints"],
            message: `Body typography on ${moment.surface.toLowerCase()} must be at least ${minimumBodySize} points.`,
        });
    if (!displayRole && moment.fontSizePoints > 32)
        context.addIssue({
            code: "custom",
            path: ["fontSizePoints"],
            message: "Body and dialogue typography cannot exceed 32 points.",
        });
    if (displayRole && moment.weight !== "SEMIBOLD")
        context.addIssue({
            code: "custom",
            path: ["weight"],
            message: "Fraunces display roles use the pinned 600 weight.",
        });
    if (moment.role === "WHISPER" && moment.fontSizePoints > 30)
        context.addIssue({
            code: "custom",
            path: ["fontSizePoints"],
            message: "A whisper treatment must remain between 29 and 30 points.",
        });
    if (moment.backgroundTone === "LIGHT" && moment.textColor !== "INK")
        context.addIssue({
            code: "custom",
            path: ["textColor"],
            message: "Light typography regions require ink text.",
        });
    if (moment.backgroundTone === "DARK" && moment.textColor !== "CREAM")
        context.addIssue({
            code: "custom",
            path: ["textColor"],
            message: "Dark typography regions require cream text.",
        });
});
function normalizedWords(value) {
    return value.normalize("NFC").replace(/\s+/gu, " ").trim().toLocaleLowerCase();
}
function regionsOverlap(left, right) {
    return !(left.x + left.width <= right.x ||
        right.x + right.width <= left.x ||
        left.y + left.height <= right.y ||
        right.y + right.height <= left.y);
}
export const spreadTypographyPlanSchema = z
    .object({
    coordinateSystem: z.enum(["TOP_LEFT_SPREAD", "EXTERNAL_RAIL"]),
    moments: z.array(typographyMomentSchema).min(1).max(3),
})
    .strict()
    .superRefine((plan, context) => {
    for (const [index, moment] of plan.moments.entries()) {
        if (plan.coordinateSystem === "EXTERNAL_RAIL") {
            if (moment.surface !== "RAIL")
                context.addIssue({
                    code: "custom",
                    path: ["moments", index, "surface"],
                    message: "External-rail typography must use the rail surface.",
                });
            continue;
        }
        const end = moment.region.x + moment.region.width;
        const remainsOnLeftPage = moment.region.x >= 0.03 && end <= 0.47;
        const remainsOnRightPage = moment.region.x >= 0.53 && end <= 0.97;
        if (!remainsOnLeftPage && !remainsOnRightPage)
            context.addIssue({
                code: "custom",
                path: ["moments", index, "region"],
                message: "Live typography must remain inside one page and clear of trim and gutter.",
            });
        if (moment.region.y < 0.04 || moment.region.y + moment.region.height > 0.96)
            context.addIssue({
                code: "custom",
                path: ["moments", index, "region"],
                message: "Live typography must remain clear of the top and bottom trim.",
            });
        for (let other = index + 1; other < plan.moments.length; other += 1)
            if (regionsOverlap(moment.region, plan.moments[other].region))
                context.addIssue({
                    code: "custom",
                    path: ["moments", other, "region"],
                    message: "Typography moments cannot overlap.",
                });
    }
});
export const protectedVisualRegionSchema = z
    .object({
    region: normalizedSafeRegionSchema,
    protects: z.string().trim().min(1).max(300),
})
    .strict()
    .superRefine((protectedRegion, context) => {
    const { region } = protectedRegion;
    const end = region.x + region.width;
    const tolerance = 1e-9;
    const remainsOnLeftPage = region.x >= 0.03 - tolerance && end <= 0.47 + tolerance;
    const remainsOnRightPage = region.x >= 0.53 - tolerance && end <= 0.97 + tolerance;
    if (!remainsOnLeftPage && !remainsOnRightPage)
        context.addIssue({
            code: "custom",
            path: ["region"],
            message: "Protected faces, hands, flower centers, and critical gestures must remain on one page and clear of trim and gutter.",
        });
    if (region.y < 0.03 || region.y + region.height > 0.97)
        context.addIssue({
            code: "custom",
            path: ["region"],
            message: "Protected visual regions must remain clear of the top and bottom trim.",
        });
});
export const temporalSequenceSchema = z
    .object({
    phases: z.array(z.string().trim().min(1).max(300)).min(2).max(3),
    panels: z.array(normalizedSafeRegionSchema).min(2).max(3),
})
    .strict()
    .superRefine((sequence, context) => {
    if (sequence.phases.length !== sequence.panels.length)
        context.addIssue({
            code: "custom",
            path: ["panels"],
            message: "Every temporal phase requires one explicit panel boundary.",
        });
    for (const [index, panel] of sequence.panels.entries())
        for (let other = index + 1; other < sequence.panels.length; other += 1)
            if (regionsOverlap(panel, sequence.panels[other]))
                context.addIssue({
                    code: "custom",
                    path: ["panels", other],
                    message: "Temporal sequence panels cannot overlap.",
                });
});
export const visualContinuitySchema = z
    .object({
    storyTime: z.string().trim().min(1).max(300),
    objectStates: z
        .array(z
        .object({
        objectId: z.string().trim().min(1).max(100),
        state: z.string().trim().min(1).max(500),
        location: z.string().trim().min(1).max(500),
    })
        .strict())
        .min(1)
        .max(16),
    forbiddenVisibleElements: z.array(z.string().trim().min(1).max(500)).min(1).max(30),
})
    .strict()
    .superRefine((continuity, context) => {
    const objectIds = continuity.objectStates.map((entry) => entry.objectId);
    if (new Set(objectIds).size !== objectIds.length)
        context.addIssue({
            code: "custom",
            path: ["objectStates"],
            message: "Each tracked continuity object must appear exactly once per spread.",
        });
});
export const showcaseTypographySystemSchema = z
    .object({
    version: z.literal("PICTURE_BOOK_TYPOGRAPHY_V3_ART_LED"),
    displayFamily: z.literal("Fraunces 600"),
    bodyFamily: z.literal("Noto Serif 400"),
    bodySemiboldFamily: z.literal("Noto Serif 600"),
    italicFamily: z.literal("Noto Serif 400 Italic"),
    minimumPaperBodyPoints: z.literal(29),
    minimumArtBodyPoints: z.literal(29),
    minimumMobileCssPixels: z.literal(16),
    maximumMomentsPerSpread: z.literal(3),
    maximumThreeMomentSpreads: z.literal(2),
    maximumWashedMoments: z.literal(2),
    minimumArtLedSpreads: z.number().int().min(5).max(8),
    automaticLineBreaks: z.literal(false),
    shadowsAllowed: z.literal(false),
})
    .strict();
export const coverTypographyPlanSchema = z
    .object({
    treatment: showcaseTypographyTreatmentSchema,
    titleLines: z.array(z.string().trim().min(1).max(100)).min(1).max(3),
    titleRegion: normalizedSafeRegionSchema,
    bylineRegion: normalizedSafeRegionSchema,
    alignment: z.enum(["LEFT", "CENTER", "RIGHT"]),
    titleSizePoints: z.number().int().min(48).max(64),
    bylineSizePoints: z.number().int().min(18).max(22),
    textColor: z.enum(["INK", "CREAM"]),
    backgroundTone: z.enum(["LIGHT", "DARK"]),
    support: z.enum(["NONE", "SUBTLE_WASH"]),
})
    .strict()
    .superRefine((plan, context) => {
    if (regionsOverlap(plan.titleRegion, plan.bylineRegion))
        context.addIssue({
            code: "custom",
            path: ["bylineRegion"],
            message: "The cover title and byline regions cannot overlap.",
        });
    if (plan.backgroundTone === "LIGHT" && plan.textColor !== "INK")
        context.addIssue({ code: "custom", path: ["textColor"], message: "Use ink on light art." });
    if (plan.backgroundTone === "DARK" && plan.textColor !== "CREAM")
        context.addIssue({
            code: "custom",
            path: ["textColor"],
            message: "Use cream on dark art.",
        });
});
export const creativeBriefSchema = z
    .object({
    id: ulid,
    bookId: ulid,
    contract: showcaseContractSchema,
    revision: z.number().int().positive(),
    mode: creativeStoryModeSchema,
    targetAgeMin: z.number().int().min(2).max(10),
    targetAgeMax: z.number().int().min(2).max(10),
    sourceStory: z.string().trim().max(50_000),
    childAgency: z.string().trim().min(1).max(1_000),
    emotionalMovement: z.string().trim().min(1).max(1_000),
    theme: z.string().trim().min(1).max(500),
    factualConstraints: z.array(z.string().trim().min(1).max(1_000)).max(100),
    safetyConstraints: z.array(z.string().trim().min(1).max(1_000)).max(100),
    visualMotifs: z.array(z.string().trim().min(1).max(300)).min(1).max(12),
    desiredWordRange: z
        .object({ minimum: z.number().int().positive(), maximum: z.number().int().positive() })
        .strict(),
    // V4 artifacts remain backward-compatible with the original twelve-spread
    // showcase while the ages 2–4 production profile uses eight spreads.
    spreadCount: z.union([z.literal(8), z.literal(12)]),
    createdAt: z.string().datetime(),
})
    .strict()
    .superRefine((brief, context) => {
    if (brief.targetAgeMax < brief.targetAgeMin)
        context.addIssue({
            code: "custom",
            path: ["targetAgeMax"],
            message: "The maximum target age cannot be below the minimum target age.",
        });
    if (brief.desiredWordRange.maximum < brief.desiredWordRange.minimum)
        context.addIssue({
            code: "custom",
            path: ["desiredWordRange", "maximum"],
            message: "The maximum word count cannot be below the minimum.",
        });
});
export const manuscriptRevisionSchema = z
    .object({
    id: ulid,
    bookId: ulid,
    contract: showcaseContractSchema,
    revision: z.number().int().positive(),
    parentRevision: z.number().int().nonnegative(),
    status: z.enum(["CANDIDATE", "CRITIQUED", "FINAL", "SUPERSEDED"]),
    title: z.string().trim().min(1).max(160),
    text: z.string().trim().min(1).max(8_000),
    spreadLines: z.array(z.string().trim().min(1).max(1_000)).min(8).max(12),
    wordCount: z.number().int().min(1).max(450),
    publicationContract: z.literal("BEDTIME_PUBLICATION_V1").optional(),
    critiques: z
        .object({
        developmental: z.array(z.string().trim().min(1).max(1_000)).max(20),
        readAloud: z.array(z.string().trim().min(1).max(1_000)).max(20),
        factual: z.array(z.string().trim().min(1).max(1_000)).max(20),
        lineEdit: z.array(z.string().trim().min(1).max(1_000)).max(20),
    })
        .strict(),
    modelId: z.string().trim().min(1).max(500),
    provider: z.enum(["ANTHROPIC", "OPENAI"]).optional(),
    fallbackUsed: z.boolean(),
    showcaseEligible: z.boolean(),
    approval: showcaseApprovalStateSchema,
    createdAt: z.string().datetime(),
})
    .strict()
    .superRefine((revision, context) => {
    const actualWords = revision.text.match(/[\p{L}\p{N}’'-]+/gu)?.length ?? 0;
    if (actualWords !== revision.wordCount)
        context.addIssue({
            code: "custom",
            path: ["wordCount"],
            message: `Recorded word count ${revision.wordCount} does not match manuscript word count ${actualWords}.`,
        });
    if (!revision.publicationContract && revision.wordCount < 70)
        context.addIssue({
            code: "custom",
            path: ["wordCount"],
            message: "A legacy manuscript must contain at least 70 words.",
        });
    if (revision.status === "FINAL" && !revision.publicationContract && revision.wordCount < 80)
        context.addIssue({
            code: "custom",
            path: ["wordCount"],
            message: "A final ages 2–4 manuscript must contain at least 80 words.",
        });
    if (revision.fallbackUsed && revision.showcaseEligible && revision.provider !== "OPENAI")
        context.addIssue({
            code: "custom",
            path: ["showcaseEligible"],
            message: "Only an explicitly identified approved production provider can make failover output showcase-eligible.",
        });
    if (revision.publicationContract &&
        (revision.bookId !== "01M3WF732AGPXZV4BFQA8DNN9H" || revision.spreadLines.length !== 8))
        context.addIssue({
            code: "custom",
            path: ["publicationContract"],
            message: "This pilot publication has eight source narrative beats and four separately composed wordless turns.",
        });
});
export const storyboardSpreadSchema = z
    .object({
    id: ulid,
    position: z.number().int().min(0).max(11),
    manuscriptText: z.string().trim().min(1).max(1_000),
    narrativePurpose: z.string().trim().min(1).max(1_000),
    emotionalBeat: z.string().trim().min(1).max(500),
    pageTurnPayoff: z.string().trim().min(1).max(500),
    composition: z.string().trim().min(1).max(2_000),
    layoutArchetype: showcaseLayoutArchetypeSchema,
    shotScale: showcaseShotScaleSchema,
    gesture: z.string().trim().min(1).max(500),
    imageOnlyInformation: z.string().trim().min(1).max(1_000),
    motif: z.string().trim().min(1).max(300),
    palettePhase: showcasePalettePhaseSchema,
    gutterPlan: z.string().trim().min(1).max(500),
    protectedVisualRegions: z.array(protectedVisualRegionSchema).min(1).max(8),
    temporalSequence: temporalSequenceSchema.nullable(),
    visualContinuity: visualContinuitySchema,
    typographyPlan: spreadTypographyPlanSchema,
})
    .strict()
    .superRefine((spread, context) => {
    const plannedText = spread.typographyPlan.moments.flatMap((moment) => moment.lines).join(" ");
    if (normalizedWords(plannedText) !== normalizedWords(spread.manuscriptText))
        context.addIssue({
            code: "custom",
            path: ["typographyPlan", "moments"],
            message: "Typography moments must contain the complete manuscript beat in reading order.",
        });
    if (spread.typographyPlan.coordinateSystem === "TOP_LEFT_SPREAD")
        for (const [momentIndex, moment] of spread.typographyPlan.moments.entries())
            for (const [protectedIndex, protectedRegion] of spread.protectedVisualRegions.entries())
                if (regionsOverlap(moment.region, protectedRegion.region))
                    context.addIssue({
                        code: "custom",
                        path: ["typographyPlan", "moments", momentIndex, "region"],
                        message: `Typography overlaps protected visual region ${protectedIndex + 1}.`,
                    });
    if (spread.temporalSequence && spread.layoutArchetype !== "SEQUENTIAL")
        context.addIssue({
            code: "custom",
            path: ["layoutArchetype"],
            message: "A controlled temporal sequence must use the sequential layout archetype.",
        });
    if (!spread.temporalSequence && spread.layoutArchetype === "SEQUENTIAL")
        context.addIssue({
            code: "custom",
            path: ["temporalSequence"],
            message: "A sequential layout requires explicit phases and panel boundaries.",
        });
});
export const artDirectionBibleSchema = z
    .object({
    id: ulid,
    bookId: ulid,
    contract: showcaseContractSchema,
    revision: z.number().int().positive(),
    medium: z.string().trim().min(1).max(500),
    visualGrammar: z.string().trim().min(1).max(3_000),
    characterContinuity: z.array(z.string().trim().min(1).max(1_000)).min(1).max(20),
    forbiddenElements: z.array(z.string().trim().min(1).max(1_000)).min(1).max(50),
    palettePhases: z
        .array(z
        .object({
        phase: showcasePalettePhaseSchema,
        colors: z
            .array(z.string().regex(/^#[0-9A-F]{6}$/i))
            .min(3)
            .max(8),
        intent: z.string().trim().min(1).max(500),
    })
        .strict())
        .min(3)
        .max(4),
    styleBoardAssetIds: z.array(ulid).max(3),
    selectedStyleBoardAssetId: ulid.optional(),
    proofAssetIds: z.array(ulid).max(3),
    approval: showcaseApprovalStateSchema,
    approvedAt: z.string().datetime().optional(),
})
    .strict();
export const layoutPlanSchema = z
    .object({
    id: ulid,
    bookId: ulid,
    contract: showcaseContractSchema,
    revision: z.number().int().positive(),
    trimWidthInches: z.literal(8.5),
    trimHeightInches: z.literal(8.5),
    interiorPageCount: z.union([z.literal(24), z.literal(28), z.literal(32)]),
    artworkPpi: z.number().int().min(300),
    bleedInches: z.number().min(0.125),
    colorSpace: z.literal("CMYK"),
    fontsEmbedded: z.literal(true),
    spreads: z.array(storyboardSpreadSchema).min(8).max(12),
    typographySystem: showcaseTypographySystemSchema,
    coverTypographyPlans: z.array(coverTypographyPlanSchema).length(3),
    coverTemplateId: z.string().trim().min(1).max(500),
    barcodeSafeRegion: normalizedSafeRegionSchema,
})
    .strict()
    .superRefine((layout, context) => {
    if (layout.interiorPageCount !== layout.spreads.length * 2 + 8)
        context.addIssue({
            code: "custom",
            path: ["interiorPageCount"],
            message: "The interior page count must include eight front/back-matter pages plus two pages per story spread.",
        });
    if (new Set(layout.coverTypographyPlans.map((plan) => plan.treatment)).size !== 3)
        context.addIssue({
            code: "custom",
            path: ["coverTypographyPlans"],
            message: "The cover proof must contain three distinct typography treatments.",
        });
    const artLedSpreads = layout.spreads.filter((spread) => [
        "FULL_BLEED",
        "VIGNETTE",
        "INTEGRATED_NEGATIVE_SPACE",
        "FACING_PAGE",
        "QUIET_BEAT",
        "SEQUENTIAL",
    ].includes(spread.layoutArchetype)).length;
    if (artLedSpreads < layout.typographySystem.minimumArtLedSpreads)
        context.addIssue({
            code: "custom",
            path: ["spreads"],
            message: `At least ${layout.typographySystem.minimumArtLedSpreads} spreads must preserve an art-led page composition with typography outside the artwork.`,
        });
    const threeMomentSpreads = layout.spreads.filter((spread) => spread.typographyPlan.moments.length === 3).length;
    if (threeMomentSpreads > layout.typographySystem.maximumThreeMomentSpreads)
        context.addIssue({
            code: "custom",
            path: ["spreads"],
            message: "No more than two spreads may fragment copy into three typography moments.",
        });
    const washedMoments = layout.spreads
        .flatMap((spread) => spread.typographyPlan.moments)
        .filter((moment) => moment.support === "SUBTLE_WASH").length;
    if (washedMoments > layout.typographySystem.maximumWashedMoments)
        context.addIssue({
            code: "custom",
            path: ["spreads"],
            message: "No more than two story moments may need a contrast wash.",
        });
});
export const qualityCriterionSchema = z.enum([
    "LITERARY_VOICE",
    "CHILD_AGENCY",
    "READ_ALOUD_RHYTHM",
    "PAGE_TURN_CONSTRUCTION",
    "VISUAL_STORYTELLING",
    "CHARACTER_PERFORMANCE",
    "COMPOSITION_AND_COLOR",
    "TYPOGRAPHY_AND_UNITY",
    "COVER_AND_PRINT",
]);
export const qualityBlockingDefectSchema = z.enum([
    "UNSUPPORTED_FACT",
    "UNSAFE_CONTENT",
    "GENERATED_TYPOGRAPHY",
    "EXTRA_CHARACTER",
    "ANATOMY_DEFECT",
    "BROKEN_CONTINUITY",
    "POOR_CROP",
    "INSUFFICIENT_RESOLUTION",
    "PRESS_PREFLIGHT_FAILED",
]);
export const qualityReviewSchema = z
    .object({
    id: ulid,
    bookId: ulid,
    contract: showcaseContractSchema,
    revision: z.number().int().positive(),
    method: z.enum(["DETERMINISTIC_DIRECT", "ANONYMIZED_SWAPPED_PAIRWISE"]),
    criteria: z
        .array(z
        .object({
        criterion: qualityCriterionSchema,
        score: z.number().int().min(1).max(5),
        evidence: z.string().trim().min(1).max(2_000),
        repair: z.string().trim().max(1_000),
    })
        .strict())
        .length(9),
    blockingDefects: z.array(qualityBlockingDefectSchema).max(30),
    flaggedSpreadPositions: z.array(z.number().int().min(0).max(11)).max(12),
    pairwiseWinner: z.enum(["SHOWCASE", "V3_SAMPLE", "TIE"]).optional(),
    positionConsistent: z.boolean().optional(),
    confidence: z.number().min(0).max(1),
    passed: z.boolean(),
    reviewedAt: z.string().datetime(),
})
    .strict()
    .superRefine((review, context) => {
    const unique = new Set(review.criteria.map((entry) => entry.criterion));
    if (unique.size !== review.criteria.length)
        context.addIssue({
            code: "custom",
            path: ["criteria"],
            message: "Each showcase quality criterion must be scored exactly once.",
        });
    const shouldPass = review.blockingDefects.length === 0 &&
        review.flaggedSpreadPositions.length === 0 &&
        review.criteria.every((entry) => entry.score >= 4) &&
        (review.pairwiseWinner === undefined || review.pairwiseWinner === "SHOWCASE");
    if (review.passed !== shouldPass)
        context.addIssue({
            code: "custom",
            path: ["passed"],
            message: "The review pass state does not match the mandatory showcase gates.",
        });
});
export const generationBudgetProviderSchema = z.enum(["ANTHROPIC", "OPENAI"]);
export const generationBudgetReservationSchema = z
    .object({
    id: ulid,
    jobId: ulid,
    action: z.string().trim().min(1).max(100),
    provider: generationBudgetProviderSchema,
    estimatedCostMicros: z.number().int().nonnegative(),
    maximumCostMicros: z.number().int().positive(),
    actualCostMicros: z.number().int().nonnegative().optional(),
    mandatory: z.boolean(),
    state: z.enum(["RESERVED", "RECONCILED", "RELEASED"]),
    createdAt: z.string().datetime(),
    reconciledAt: z.string().datetime().optional(),
})
    .strict();
const providerSpendSchema = z
    .object({
    estimatedCostMicros: z.number().int().nonnegative(),
    reservedCostMicros: z.number().int().nonnegative(),
    actualCostMicros: z.number().int().nonnegative(),
})
    .strict();
export const generationBudgetSchema = z
    .object({
    bookId: ulid,
    contract: showcaseContractSchema,
    revision: z.number().int().nonnegative(),
    dollarLimitMicros: z.number().int().positive().max(30_000_000),
    estimatedCostMicros: z.number().int().nonnegative(),
    reservedCostMicros: z.number().int().nonnegative(),
    actualCostMicros: z.number().int().nonnegative(),
    remainingCostMicros: z.number().int().nonnegative(),
    mandatoryArtworkReserveMicros: z.number().int().nonnegative(),
    providerBreakdown: z
        .object({ ANTHROPIC: providerSpendSchema, OPENAI: providerSpendSchema })
        .strict(),
    reservations: z.array(generationBudgetReservationSchema).max(100),
    pricingVersionTimestamp: z.string().datetime(),
    exhaustionState: z.enum(["ACTIVE", "DOLLAR_EXHAUSTED", "ELEVENLABS_EXHAUSTED", "COMPLETE"]),
    elevenLabs: z
        .object({
        availableCredits: z.number().int().nonnegative(),
        reservedCredits: z.number().int().nonnegative(),
        consumedCredits: z.number().int().nonnegative(),
        remainingCredits: z.number().int().nonnegative(),
        protectedReserveCredits: z.literal(2_000),
        narrationLimitCredits: z.number().int().min(0).max(5_000),
    })
        .strict(),
    updatedAt: z.string().datetime(),
})
    .strict()
    .superRefine((budget, context) => {
    if (budget.actualCostMicros + budget.reservedCostMicros + budget.remainingCostMicros !==
        budget.dollarLimitMicros)
        context.addIssue({
            code: "custom",
            path: ["remainingCostMicros"],
            message: "Actual, reserved, and remaining spend must equal the hard dollar limit.",
        });
    if (budget.elevenLabs.consumedCredits +
        budget.elevenLabs.reservedCredits +
        budget.elevenLabs.remainingCredits !==
        budget.elevenLabs.availableCredits)
        context.addIssue({
            code: "custom",
            path: ["elevenLabs", "remainingCredits"],
            message: "Consumed, reserved, and remaining narration credits must equal availability.",
        });
});
export const putStoryBriefCommandSchema = storyBriefSchema
    .omit({ bookId: true, revision: true, updatedAt: true })
    .extend({ expectedRevision: z.number().int().nonnegative() })
    .strict();
/** A reservation maximum, not a provider price promise or content approval. */
export const storyIntentAssistanceAuthorizationSchema = z
    .object({
    policyVersion: z.enum(["2026-10-02.1", "2026-10-02.2"]),
    quoteId: sha256,
    quotedCostMicros: z.literal(250_000),
    expectedAllowanceRevision: z.number().int().nonnegative(),
    expectedAllowanceExists: z.boolean(),
    sourceDigestSha256: sha256,
    providerRequestDigestSha256: sha256,
})
    .strict();
export const storyIntentAssistanceOfferSchema = z
    .object({
    quote: storyIntentAssistanceAuthorizationSchema.extend({
        modelId: z.string().min(1).max(500),
        requestBytes: z
            .number()
            .int()
            .positive()
            .max(48 * 1_024),
        maximumHttpAttempts: z.literal(1),
        maximumOutputTokens: z.literal(3_200),
        ownerCeilingMicros: z.union([z.literal(10_000_000), z.literal(30_000_000)]),
        truthBoundary: z.literal("VERSIONED_MAXIMUM_RESERVATION_NOT_PROVIDER_PRICE_OR_CONTENT_APPROVAL"),
    }),
    command: z
        .object({
        action: z.literal("INTENT_GRAPH"),
        expectedRevision: z.number().int().nonnegative(),
        expectedIntentRevision: z.number().int().nonnegative(),
        intentTrigger: z.literal("HELP_ME_SHAPE"),
        assistanceAuthorization: storyIntentAssistanceAuthorizationSchema,
    })
        .strict(),
})
    .strict();
export const storyIntentAssistanceAllowanceSchema = z
    .object({
    offer: storyIntentAssistanceOfferSchema.nullable(),
    blockingDiagnostics: z
        .array(z
        .object({
        code: z.string().min(1).max(100),
        message: z.string().min(1).max(1_000),
    })
        .strict())
        .max(20),
})
    .strict();
/** Visual discovery has its own bounded request and monetary authority. */
export const storyVisualDiscoveryAuthorizationSchema = storyIntentAssistanceAuthorizationSchema
    .extend({
    policyVersion: z.literal("2026-10-04.1"),
    quotedCostMicros: z.number().int().positive().max(262_000),
    maximumInputTokens: z.number().int().positive().max(57_344),
})
    .strict();
export const storyVisualDiscoveryOfferSchema = z
    .object({
    quote: storyVisualDiscoveryAuthorizationSchema.extend({
        modelId: z.string().min(1).max(500),
        requestBytes: z
            .number()
            .int()
            .positive()
            .max(48 * 1_024),
        maximumHttpAttempts: z.literal(1),
        maximumOutputTokens: z.literal(3_200),
        ownerCeilingMicros: z.union([z.literal(10_000_000), z.literal(30_000_000)]),
        truthBoundary: z.literal("VERSIONED_MAXIMUM_RESERVATION_NOT_PROVIDER_PRICE_OR_CONTENT_APPROVAL"),
    }),
    command: z
        .object({
        action: z.literal("VISUAL_DISCOVERY"),
        expectedRevision: z.number().int().nonnegative(),
        expectedIntentRevision: z.number().int().nonnegative(),
        expectedVisualRevision: z.number().int().nonnegative(),
        intentTrigger: z.enum(["DISCOVER_VISUALS", "REFRESH_VISUALS"]),
        visualAuthorization: storyVisualDiscoveryAuthorizationSchema,
    })
        .strict(),
})
    .strict();
export const storyVisualDiscoveryAllowanceSchema = storyIntentAssistanceAllowanceSchema.extend({
    offer: storyVisualDiscoveryOfferSchema.nullable(),
    recovery: storyVisualFailureRecoveryOfferSchema.nullable().optional(),
});
export const generateStoryCommandSchema = z
    .object({
    action: storyGenerationActionSchema,
    expectedRevision: z.number().int().nonnegative(),
    expectedIntentRevision: z.number().int().nonnegative().optional(),
    expectedVisualRevision: z.number().int().nonnegative().optional(),
    assistanceAuthorization: storyIntentAssistanceAuthorizationSchema.optional(),
    visualAuthorization: storyVisualDiscoveryAuthorizationSchema.optional(),
    intentTrigger: z
        .enum([
        "SAVED_CHECKPOINT",
        "RECORDING_TRANSCRIBED",
        "HELP_ME_SHAPE",
        "REFLECT_CHANGES",
        "ASK_ONE_QUESTION",
        "DISCOVER_VISUALS",
        "REFRESH_VISUALS",
    ])
        .optional(),
    instruction: z.string().trim().max(2_000).optional(),
    preset: z.enum(["SIMPLIFY", "WARMER", "ADD_DIALOGUE"]).optional(),
    outline: z.array(storyOutlineBeatSchema).min(1).max(16).optional(),
})
    .strict()
    .superRefine((command, context) => {
    if (command.visualAuthorization && command.action !== "VISUAL_DISCOVERY")
        context.addIssue({
            code: "custom",
            path: ["visualAuthorization"],
            message: "This reservation applies only to visual discovery.",
        });
    if (command.assistanceAuthorization && command.action !== "INTENT_GRAPH")
        context.addIssue({
            code: "custom",
            path: ["assistanceAuthorization"],
            message: "This exact one-action reservation applies only to Story Intent assistance.",
        });
    const intentAction = ["INTENT_GRAPH", "INTENT_CLARIFICATION"].includes(command.action);
    const visualAction = command.action === "VISUAL_DISCOVERY";
    const assistedAction = intentAction || visualAction;
    if (assistedAction && command.expectedIntentRevision === undefined)
        context.addIssue({
            code: "custom",
            path: ["expectedIntentRevision"],
            message: "Assisted direction must be bound to the Story Intent revision reviewed.",
        });
    if (visualAction && command.expectedVisualRevision === undefined)
        context.addIssue({
            code: "custom",
            path: ["expectedVisualRevision"],
            message: "Visual discovery must be bound to the visual revision the customer reviewed.",
        });
    if (assistedAction && !command.intentTrigger)
        context.addIssue({
            code: "custom",
            path: ["intentTrigger"],
            message: "Assisted direction requires an explicit customer or checkpoint trigger.",
        });
    if (!visualAction && command.expectedVisualRevision !== undefined)
        context.addIssue({
            code: "custom",
            path: ["expectedVisualRevision"],
            message: "A visual revision applies only to Visual Discovery.",
        });
    if (!assistedAction && (command.expectedIntentRevision !== undefined || command.intentTrigger))
        context.addIssue({
            code: "custom",
            path: ["action"],
            message: "Assisted-direction fields apply only to Story Intent or Visual Discovery.",
        });
});
export const updateStoryIntentCommandSchema = z
    .object({
    expectedStoryRevision: z.number().int().nonnegative(),
    expectedIntentRevision: z.number().int().positive(),
    operation: z.discriminatedUnion("type", [
        z.object({ type: z.literal("ACCEPT_SUGGESTION"), nodeId: ulid }).strict(),
        z.object({ type: z.literal("DISMISS_SUGGESTION"), nodeId: ulid }).strict(),
        z.object({ type: z.literal("UNDO_SUGGESTION"), nodeId: ulid }).strict(),
        z
            .object({
            type: z.literal("EDIT_NODE"),
            nodeId: ulid,
            detail: z.string().trim().min(1).max(1_000),
        })
            .strict(),
        z
            .object({
            type: z.literal("EDIT_MIRROR"),
            summary: z.string().trim().min(1).max(700),
        })
            .strict(),
        z
            .object({
            type: z.literal("ANSWER_CLARIFICATION"),
            answer: z.string().trim().min(1).max(2_000),
        })
            .strict(),
        z.object({ type: z.literal("DISMISS_CLARIFICATION") }).strict(),
        z
            .object({
            type: z.literal("APPROVE_PROMISE"),
            promise: storyPromiseSchema
                .omit({ status: true, approvedAt: true, approvedBy: true })
                .strict(),
        })
            .strict(),
    ]),
})
    .strict();
export const updateStoryVisualDiscoveryCommandSchema = z
    .object({
    expectedStoryRevision: z.number().int().nonnegative(),
    expectedIntentRevision: z.number().int().positive(),
    expectedVisualRevision: z.number().int().positive(),
    operation: z.discriminatedUnion("type", [
        z.object({ type: z.literal("ACCEPT_VISUAL_DIRECTION"), directionId: ulid }).strict(),
        z.object({ type: z.literal("DISMISS_VISUAL_DIRECTION"), directionId: ulid }).strict(),
        z.object({ type: z.literal("UNDO_VISUAL_DIRECTION"), directionId: ulid }).strict(),
        z
            .object({
            type: z.literal("EDIT_VISUAL_DIRECTION"),
            directionId: ulid,
            direction: storyVisualDirectionContentSchema,
        })
            .strict(),
        z
            .object({
            type: z.literal("APPROVE_VISUAL_PROMISE"),
            promise: storyVisualPromiseSchema
                .omit({ status: true, approvedAt: true, approvedBy: true })
                .strict(),
        })
            .strict(),
    ]),
})
    .strict();
export const showcaseGenerationActionSchema = z.enum([
    "CONCEPTS",
    "MANUSCRIPT_CANDIDATES",
    "MANUSCRIPT_CRITIQUE",
    "MANUSCRIPT_FINAL",
    "STORYBOARD",
    "STYLE_BOARD_CANDIDATES",
    "REPRESENTATIVE_PROOFS",
    "FINAL_SPREAD",
    "COVER_CONCEPTS",
    "FINAL_COVER",
    "SEQUENCE_EVALUATION",
    "TARGETED_REPAIR",
    "NARRATION_FINAL",
    "RENDER_SHOWCASE",
]);
export const generateStoryV4CommandSchema = z
    .object({
    contract: showcaseContractSchema,
    action: showcaseGenerationActionSchema,
    expectedRevision: z.number().int().nonnegative(),
    fixture: z.boolean().default(true),
    meteredShowcaseRun: z.literal(true).optional(),
    spreadPosition: z.number().int().min(0).max(11).optional(),
    candidateId: ulid.optional(),
    candidateCount: z.number().int().min(1).max(3).optional(),
    coverFeedback: z
        .object({
        sourceArtifactId: ulid,
        instruction: z.string().trim().min(1).max(600),
        referenceAssetIds: z
            .array(ulid)
            .max(2)
            .refine((ids) => new Set(ids).size === ids.length),
    })
        .strict()
        .optional(),
    voiceId: z.string().trim().min(1).max(200).optional(),
    voiceName: z.string().trim().min(1).max(200).optional(),
    approvalStage: showcaseApprovalStageSchema.optional(),
    userRepair: z
        .object({
        operation: z.enum([
            "REMOVE",
            "REPLACE",
            "EXPRESSION",
            "RECOLOR",
            "MOVE",
            "ANATOMY",
            "CUSTOM",
        ]),
        instruction: z.string().trim().max(600).default(""),
        selection: z
            .object({
            kind: z.literal("POLYGON"),
            points: z
                .array(z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }).strict())
                .min(3)
                .max(96),
        })
            .strict(),
    })
        .strict()
        .optional(),
})
    .strict()
    .superRefine((command, context) => {
    if (command.coverFeedback && command.action !== "COVER_CONCEPTS")
        context.addIssue({
            code: "custom",
            path: ["coverFeedback"],
            message: "Cover feedback applies only to cover ideas.",
        });
    if (["FINAL_SPREAD", "TARGETED_REPAIR"].includes(command.action) &&
        command.spreadPosition === undefined)
        context.addIssue({
            code: "custom",
            path: ["spreadPosition"],
            message: "This showcase action requires a spread position.",
        });
    if (command.action === "FINAL_COVER" && !command.candidateId)
        context.addIssue({
            code: "custom",
            path: ["candidateId"],
            message: "The final cover requires the approved cover direction.",
        });
    if (command.meteredShowcaseRun && command.fixture)
        context.addIssue({
            code: "custom",
            path: ["fixture"],
            message: "A metered showcase run must explicitly disable provider fixtures.",
        });
    if (command.action === "NARRATION_FINAL" &&
        !command.fixture &&
        (!command.voiceId || !command.meteredShowcaseRun))
        context.addIssue({
            code: "custom",
            path: ["voiceId"],
            message: "The one metered final narration requires an explicitly reviewed voice.",
        });
    if (command.action !== "NARRATION_FINAL" && (command.voiceId || command.voiceName))
        context.addIssue({
            code: "custom",
            path: ["voiceId"],
            message: "Voice selection applies only to the final narration action.",
        });
    if (command.candidateCount !== undefined &&
        !["STYLE_BOARD_CANDIDATES", "REPRESENTATIVE_PROOFS", "COVER_CONCEPTS"].includes(command.action))
        context.addIssue({
            code: "custom",
            path: ["candidateCount"],
            message: "Candidate count applies only to optional visual exploration actions.",
        });
    if (command.userRepair && command.action !== "TARGETED_REPAIR")
        context.addIssue({
            code: "custom",
            path: ["userRepair"],
            message: "A selected-region repair applies only to a targeted repair action.",
        });
});
export const updateStoryBlueprintCommandSchema = z
    .object({
    expectedRevision: z.number().int().nonnegative(),
    sourceArtifactId: ulid,
    title: z.string().trim().min(1).max(160),
    // Eight pages for a book on the first profile, twelve for a picture book (2026-11-02). The
    // server keeps the page count of the manuscript being edited.
    spreadLines: z.array(z.string().trim().min(1).max(1_000)).min(8).max(12),
})
    .strict();
export const restoreStoryArtworkCommandSchema = z
    .object({
    expectedRevision: z.number().int().nonnegative(),
    spreadPosition: z.number().int().min(0).max(11),
    sourceArtifactId: ulid,
})
    .strict();
export const bedtimePublicationGenerationCommandSchema = z
    .object({
    contract: z.literal("BEDTIME_PUBLICATION_V1"),
    action: z.enum([
        "REPRESENTATIVE_LAYOUT_PROOFS",
        "TARGETED_PUBLICATION_PROOF_REPLACEMENT",
        "NATIVE_PUBLICATION_MEDIA",
        "NATIVE_PUBLICATION_COVER",
        "CANONICAL_PUBLICATION_RENDER",
    ]),
    expectedRevision: z.number().int().nonnegative(),
    expectedPlanRevision: z.number().int().positive(),
    expectedPlanDigestSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    expectedSourceRevision: z.number().int().nonnegative(),
    expectedProfileRevision: z.number().int().nonnegative(),
    expectedConsentRevision: z.number().int().nonnegative(),
    expectedApprovalRevision: z.number().int().nonnegative(),
    authorizationDigestSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    zeroSpendCompilationDigestSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
    studioPlanPreviewDigestSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
    existingArtProofDigestSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
    quoteId: z.string().trim().min(1).max(200).optional(),
    quotedCostMicros: z.number().int().nonnegative().optional(),
    expectedAllowanceRevision: z.number().int().nonnegative().optional(),
    proofCount: z.union([z.literal(1), z.literal(3)]).optional(),
    expectedBookRevision: z.number().int().nonnegative().optional(),
    expectedCanonicalOutputAuthorityDigestSha256: sha256.optional(),
    expectedNativeManifestDigestSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
    expectedNativeCoverRequestDigestSha256: sha256.optional(),
    expectedRepresentativeReviewDigestSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
    nativeUseIds: z.array(z.string().trim().min(1).max(240)).min(1).max(3).optional(),
    sourceProofSetId: z.string().trim().min(1).max(240).optional(),
    replacementOfReviewId: z.string().trim().min(1).max(240).optional(),
    expectedReviewDigestSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
    expectedAssetId: z.string().trim().min(1).max(240).optional(),
    expectedAssetObjectVersionId: z.string().trim().min(1).max(1_000).optional(),
    expectedAssetChecksumSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
    replacementReasonCodes: z.array(z.string().trim().min(1).max(120)).min(1).max(12).optional(),
    replacementInstruction: z.string().trim().min(1).max(2_000).optional(),
    // Only final native pictures may carry a reserved words'-area repaint, as quoted.
    allowAutomaticRepairs: z.boolean().default(false),
    /**
     * Guided only: once this batch is painted, the server paints the next pages never painted
     * yet, so the buyer can close the page (owner-approved, 2026-10-14). Never a repaint.
     */
    continueAutomatically: z.boolean().optional(),
})
    .strict()
    .superRefine((command, context) => {
    const proof = command.action === "REPRESENTATIVE_LAYOUT_PROOFS";
    const replacement = command.action === "TARGETED_PUBLICATION_PROOF_REPLACEMENT";
    const native = command.action === "NATIVE_PUBLICATION_MEDIA";
    if (command.allowAutomaticRepairs && !native)
        context.addIssue({
            code: "custom",
            path: ["allowAutomaticRepairs"],
            message: "Only final native pictures may reserve an automatic repair.",
        });
    if (command.continueAutomatically && !native)
        context.addIssue({
            code: "custom",
            path: ["continueAutomatically"],
            message: "Only final native pictures carry on painting by themselves.",
        });
    const cover = command.action === "NATIVE_PUBLICATION_COVER";
    const digitalProof = command.action === "CANONICAL_PUBLICATION_RENDER";
    const paid = proof || replacement || native || cover;
    if (cover &&
        (!command.expectedNativeCoverRequestDigestSha256 ||
            command.expectedBookRevision === undefined ||
            !command.expectedRepresentativeReviewDigestSha256 ||
            !command.quoteId ||
            command.quotedCostMicros === undefined ||
            command.expectedAllowanceRevision === undefined ||
            command.nativeUseIds ||
            command.expectedNativeManifestDigestSha256 ||
            command.proofCount !== undefined ||
            command.zeroSpendCompilationDigestSha256 ||
            command.studioPlanPreviewDigestSha256 ||
            command.existingArtProofDigestSha256))
        context.addIssue({
            code: "custom",
            path: ["expectedNativeCoverRequestDigestSha256"],
            message: "One native cover needs its exact current request/source, reviewed representative gate, allowance and actor-bound quote; never an inferred batch.",
        });
    if (!cover && command.expectedNativeCoverRequestDigestSha256)
        context.addIssue({
            code: "custom",
            path: ["expectedNativeCoverRequestDigestSha256"],
            message: "Cover request authority applies only to a separately authorized one-image cover action.",
        });
    if (native &&
        (!command.quoteId ||
            command.quotedCostMicros === undefined ||
            command.expectedAllowanceRevision === undefined ||
            command.expectedBookRevision === undefined ||
            !command.expectedNativeManifestDigestSha256 ||
            !command.expectedRepresentativeReviewDigestSha256 ||
            !command.nativeUseIds ||
            new Set(command.nativeUseIds).size !== command.nativeUseIds.length ||
            command.proofCount !== undefined ||
            command.zeroSpendCompilationDigestSha256 ||
            command.studioPlanPreviewDigestSha256 ||
            command.existingArtProofDigestSha256))
        context.addIssue({
            code: "custom",
            path: ["nativeUseIds"],
            message: "Native illustrations require one to three distinct exact uses, the current manifest/review/allowance quote and source-bound authorization; never an inferred full batch.",
        });
    if (!native &&
        ((!digitalProof && !cover && !proof && command.expectedBookRevision !== undefined) ||
            command.expectedNativeManifestDigestSha256 ||
            (!cover && command.expectedRepresentativeReviewDigestSha256) ||
            command.nativeUseIds))
        context.addIssue({
            code: "custom",
            path: ["nativeUseIds"],
            message: "Native-source fields apply only to an explicitly authorized native illustration action.",
        });
    if (digitalProof &&
        (command.expectedBookRevision === undefined ||
            !command.expectedCanonicalOutputAuthorityDigestSha256))
        context.addIssue({
            code: "custom",
            path: ["expectedBookRevision"],
            message: "An exact digital proof requires the current book revision and server-bound source authorization.",
        });
    if (!digitalProof && command.expectedCanonicalOutputAuthorityDigestSha256)
        context.addIssue({
            code: "custom",
            path: ["expectedCanonicalOutputAuthorityDigestSha256"],
            message: "Canonical output authority applies only to the exact zero-provider composed proof.",
        });
    if (proof &&
        (!command.quoteId ||
            command.quotedCostMicros === undefined ||
            command.expectedAllowanceRevision === undefined ||
            command.proofCount !== 3 ||
            !command.zeroSpendCompilationDigestSha256 ||
            !command.studioPlanPreviewDigestSha256 ||
            !command.existingArtProofDigestSha256))
        context.addIssue({
            code: "custom",
            path: ["proofCount"],
            message: "Representative publication proofs require one current quote, allowance revision, and exactly three images.",
        });
    if (replacement &&
        (!command.quoteId ||
            command.quotedCostMicros === undefined ||
            command.expectedAllowanceRevision === undefined ||
            command.proofCount !== 1 ||
            !command.sourceProofSetId ||
            !command.replacementOfReviewId ||
            !command.expectedReviewDigestSha256 ||
            !command.expectedAssetId ||
            !command.expectedAssetObjectVersionId ||
            !command.expectedAssetChecksumSha256 ||
            !command.replacementReasonCodes ||
            !command.replacementInstruction))
        context.addIssue({
            code: "custom",
            path: ["replacementOfReviewId"],
            message: "A targeted publication-proof replacement requires one rejected checksum-bound parent, one current quote, and exactly one image.",
        });
    if (replacement &&
        (command.zeroSpendCompilationDigestSha256 ||
            command.studioPlanPreviewDigestSha256 ||
            command.existingArtProofDigestSha256))
        context.addIssue({
            code: "custom",
            path: ["zeroSpendCompilationDigestSha256"],
            message: "Initial representative-proof gate digests do not apply to a checksum-bound targeted replacement.",
        });
    if (!paid &&
        (command.quoteId ||
            command.quotedCostMicros !== undefined ||
            command.expectedAllowanceRevision !== undefined ||
            command.proofCount !== undefined ||
            command.zeroSpendCompilationDigestSha256 ||
            command.studioPlanPreviewDigestSha256 ||
            command.existingArtProofDigestSha256))
        context.addIssue({
            code: "custom",
            path: ["quoteId"],
            message: "Provider quote fields apply only to representative proofs and targeted replacements.",
        });
    if (!replacement &&
        (command.sourceProofSetId ||
            command.replacementOfReviewId ||
            command.expectedReviewDigestSha256 ||
            command.expectedAssetId ||
            command.expectedAssetObjectVersionId ||
            command.expectedAssetChecksumSha256 ||
            command.replacementReasonCodes ||
            command.replacementInstruction))
        context.addIssue({
            code: "custom",
            path: ["replacementOfReviewId"],
            message: "Replacement lineage fields apply only to targeted proof replacements.",
        });
});
export const bedtimeNativeMediaReadV1Schema = z
    .object({
    failureRecovery: bedtimeNativeFailureRecoveryOfferSchema.nullable().optional(),
    manifest: bedtimeNativeMediaManifestV1Schema,
    representativeReady: z.boolean(),
    representativeReviewDigestSha256: sha256,
    allowanceReady: z.boolean(),
    nativeReservationPending: z.boolean(),
    providerEnabled: z.boolean(),
    providerDispatchAuthorized: z.literal(false),
    offer: z
        .object({
        quote: z
            .object({
            action: z.literal("NATIVE_PUBLICATION_MEDIA"),
            manifestDigestSha256: sha256,
            selectedUseIds: z.array(z.string().min(1).max(240)).min(1).max(3),
            allowanceRevision: z.number().int().nonnegative(),
            quality: z.literal("medium"),
            modelId: bedtimeNativeImageModelIdSchema,
            quotedCostMicros: z.number().int().positive(),
            allowAutomaticRepairs: z.boolean(),
            quoteId: sha256,
            priceMeaning: z.literal("MAXIMUM_RESERVATION_NOT_EXPECTED_OR_PROVIDER_GUARANTEED_COST"),
        })
            .strict(),
        command: bedtimePublicationGenerationCommandSchema,
    })
        .strict()
        .nullable(),
})
    .strict()
    .superRefine((read, context) => {
    if (!read.offer)
        return;
    const { quote, command } = read.offer;
    if (!read.representativeReady ||
        !read.allowanceReady ||
        read.nativeReservationPending ||
        command.action !== "NATIVE_PUBLICATION_MEDIA" ||
        quote.manifestDigestSha256 !== read.manifest.manifestDigestSha256 ||
        quote.modelId !== read.manifest.modelId ||
        command.expectedPlanDigestSha256 !== read.manifest.planDigestSha256 ||
        command.expectedNativeManifestDigestSha256 !== read.manifest.manifestDigestSha256 ||
        command.expectedRepresentativeReviewDigestSha256 !== read.representativeReviewDigestSha256 ||
        command.quoteId !== quote.quoteId ||
        command.quotedCostMicros !== quote.quotedCostMicros ||
        command.allowAutomaticRepairs !== quote.allowAutomaticRepairs ||
        command.expectedAllowanceRevision !== quote.allowanceRevision ||
        JSON.stringify(command.nativeUseIds) !== JSON.stringify(quote.selectedUseIds) ||
        new Set(quote.selectedUseIds).size !== quote.selectedUseIds.length ||
        quote.selectedUseIds.some((useId) => !read.manifest.requests.some((request) => request.useId === useId && request.eligibility === "SUPPORTED_NATIVE_REQUEST")))
        context.addIssue({
            code: "custom",
            path: ["offer"],
            message: "The native offer must bind this exact eligible plan, selection, review and allowance.",
        });
});
export const bedtimeNativeCoverReadV1Schema = z
    .object({
    request: bedtimeNativeCoverRequestV1Schema.nullable(),
    representativeReady: z.boolean(),
    representativeReviewDigestSha256: sha256,
    allowanceReady: z.boolean(),
    nativeReservationPending: z.boolean(),
    providerEnabled: z.boolean(),
    providerDispatchAuthorized: z.literal(false),
    blockingDiagnostics: z
        .array(z
        .object({ code: z.string().min(1).max(160), message: z.string().min(1).max(2000) })
        .strict())
        .max(20),
    offer: z
        .object({
        quote: z
            .object({
            quoteId: sha256,
            quotedCostMicros: z.literal(750000),
            quality: z.literal("medium"),
            imageCount: z.literal(1),
            priceMeaning: z.literal("MAXIMUM_RESERVATION_NOT_EXPECTED_OR_PROVIDER_GUARANTEED_COST"),
        })
            .strict(),
        command: bedtimePublicationGenerationCommandSchema,
    })
        .strict()
        .nullable(),
})
    .strict()
    .superRefine((read, context) => {
    const offer = read.offer;
    if (offer &&
        (!read.request ||
            read.request.eligibility !== "SUPPORTED_NATIVE_REQUEST" ||
            read.blockingDiagnostics.length ||
            !read.representativeReady ||
            !read.allowanceReady ||
            read.nativeReservationPending ||
            offer.command.action !== "NATIVE_PUBLICATION_COVER" ||
            offer.command.expectedNativeCoverRequestDigestSha256 !== read.request.requestDigestSha256 ||
            offer.command.expectedPlanDigestSha256 !== read.request.planDigestSha256 ||
            offer.command.expectedRepresentativeReviewDigestSha256 !==
                read.representativeReviewDigestSha256 ||
            offer.command.quoteId !== offer.quote.quoteId ||
            offer.command.quotedCostMicros !== offer.quote.quotedCostMicros))
        context.addIssue({
            code: "custom",
            path: ["offer"],
            message: "The one-image cover offer must bind the exact eligible native source, current plan/review and allowance.",
        });
});
export const bedtimeDigitalProofOfferV1Schema = z
    .object({
    contract: z.literal("BEDTIME_DIGITAL_PROOF_OFFER_V1"),
    bookId: ulid,
    bookRevision: z.number().int().nonnegative(),
    planDigestSha256: sha256,
    canonicalOutputAuthorityDigestSha256: sha256,
    providerCostMicros: z.literal(0),
    providerDispatchAuthorized: z.literal(false),
    truthBoundary: z.literal("CURRENT_SOURCE_PROOF_REQUEST_NOT_COMPLETE_EDITION_OR_PRINT_APPROVAL"),
    command: bedtimePublicationGenerationCommandSchema,
})
    .strict()
    .superRefine((offer, context) => {
    if (offer.command.action !== "CANONICAL_PUBLICATION_RENDER" ||
        offer.command.expectedPlanDigestSha256 !== offer.planDigestSha256 ||
        offer.command.expectedBookRevision !== offer.bookRevision ||
        offer.command.expectedCanonicalOutputAuthorityDigestSha256 !==
            offer.canonicalOutputAuthorityDigestSha256)
        context.addIssue({
            code: "custom",
            path: ["command"],
            message: "The digital proof command must bind this exact book, plan and zero-provider action.",
        });
});
export const bedtimeDigitalProofReadV1Schema = z
    .object({
    offer: bedtimeDigitalProofOfferV1Schema.nullable(),
    blockingDiagnostics: z
        .array(z
        .object({ code: z.string().min(1).max(160), message: z.string().min(1).max(2000) })
        .strict())
        .max(50),
    providerDispatchAuthorized: z.literal(false),
})
    .strict()
    .superRefine((read, context) => {
    if ((read.offer !== null) !== (read.blockingDiagnostics.length === 0))
        context.addIssue({
            code: "custom",
            path: ["offer"],
            message: "A blocked current output source cannot expose an executable proof offer.",
        });
});
export const storyGenerationRequestSchema = z.union([
    generateStoryCommandSchema,
    generateStoryV4CommandSchema,
    bedtimePublicationGenerationCommandSchema,
]);
export const approveShowcaseStageCommandSchema = z
    .object({
    contract: showcaseContractSchema,
    stage: showcaseApprovalStageSchema,
    expectedRevision: z.number().int().nonnegative(),
    approvedArtifactIds: z.array(ulid).min(1).max(20),
    selectedCoverAssetId: ulid.optional(),
})
    .strict()
    .superRefine((command, context) => {
    if (command.stage === "STORYBOARD_STYLE_BOARD" && !command.selectedCoverAssetId)
        context.addIssue({
            code: "custom",
            path: ["selectedCoverAssetId"],
            message: "Choose a cover direction before approving the visual direction.",
        });
    if (command.stage !== "STORYBOARD_STYLE_BOARD" && command.selectedCoverAssetId)
        context.addIssue({
            code: "custom",
            path: ["selectedCoverAssetId"],
            message: "Cover direction selection belongs to the visual-direction approval.",
        });
});
export const startStoryTranscriptionCommandSchema = z
    .object({
    assetId: ulid,
    expectedRevision: z.number().int().nonnegative(),
    diarize: z.boolean().default(true),
    tagAudioEvents: z.boolean().default(true),
    audioProviderDisclosureAccepted: z.literal(true),
})
    .strict();
export const patchStorySpreadCommandSchema = z
    .object({
    expectedRevision: z.number().int().positive(),
    text: z.string().trim().min(1).max(5_000).optional(),
    sourceConfirmed: z.boolean().optional(),
    visualDirection: z.string().trim().min(1).max(2_000).optional(),
    selectedIllustrationId: ulid.optional(),
})
    .strict()
    .refine((command) => command.text !== undefined ||
    command.visualDirection !== undefined ||
    command.selectedIllustrationId !== undefined, "Provide a spread change.");
export const approveStoryRevisionCommandSchema = z
    .object({ expectedRevision: z.number().int().positive() })
    .strict();
export const generateIllustrationCommandSchema = z
    .object({
    expectedRevision: z.number().int().positive(),
    direction: z.string().trim().max(2_000).optional(),
    approveCharacterSheet: z.boolean().default(false),
})
    .strict();
export const generateNarrationCommandSchema = z
    .object({
    expectedRevision: z.number().int().positive(),
    source: z.enum(["RECORDED", "SYNTHETIC", "VOICE_CHANGED"]),
    voiceId: z.string().trim().min(1).max(200).optional(),
    voiceName: z.string().trim().min(1).max(200).optional(),
    model: z.enum(["EXPRESSIVE", "CONSISTENT", "FAST"]).default("CONSISTENT"),
    performance: z.enum(["GENTLE", "WHISPERED", "PLAYFUL", "DRAMATIC"]).default("GENTLE"),
    stability: z.number().min(0).max(1).default(0.55),
    similarityBoost: z.number().min(0).max(1).default(0.75),
    style: z.number().min(0).max(1).default(0.15),
    speed: z.number().min(0.7).max(1.2).default(0.92),
    speakerBoost: z.boolean().default(true),
    removeBackgroundNoise: z.boolean().default(true),
    voiceConsentAttested: z.boolean().default(false),
    sourceAssetId: ulid.optional(),
    spreadId: ulid.optional(),
    createShare: z.boolean().default(false),
})
    .strict()
    .superRefine((command, context) => {
    if (command.source !== "RECORDED" && !command.voiceId)
        context.addIssue({
            code: "custom",
            path: ["voiceId"],
            message: "Synthetic and transformed narration require an ElevenLabs voice.",
        });
    if (command.source !== "SYNTHETIC" && !command.sourceAssetId)
        context.addIssue({
            code: "custom",
            path: ["sourceAssetId"],
            message: "Recorded and transformed narration require a private audio recording.",
        });
    if (command.source === "VOICE_CHANGED" && !command.voiceConsentAttested)
        context.addIssue({
            code: "custom",
            path: ["voiceConsentAttested"],
            message: "Voice transformation requires consent from the recorded speaker.",
        });
});
export const generateSoundscapeCommandSchema = z
    .object({
    expectedRevision: z.number().int().positive(),
    prompt: z.string().trim().min(10).max(500),
    durationSeconds: z.number().min(5).max(30).default(20),
    loop: z.boolean().default(true),
    promptInfluence: z.number().min(0).max(1).default(0.35),
})
    .strict();
export const upgradeStoryCommandSchema = z
    .object({ expectedBookRevision: z.number().int().nonnegative() })
    .strict();
export function createStoryFactReview(brief, confirmedAt) {
    return storyFactReviewSchema.parse({
        version: "STORY_FACT_REVIEW_V1",
        intendedChildName: brief.intendedChildName,
        intendedChildRelationship: brief.intendedChildRelationship,
        ageRange: brief.ageRange,
        characters: brief.characters.map(({ id, name, role, appearance }) => ({
            id,
            name,
            role,
            appearance,
        })),
        storytellerAttribution: brief.storytellerAttribution,
        dedication: brief.dedication,
        mustInclude: brief.experience?.mustInclude ?? [],
        pronunciations: brief.pronunciations ?? [],
        confirmedAt,
    });
}
export function storyFactReviewMatchesBrief(brief) {
    if (!brief.factReview)
        return false;
    const current = createStoryFactReview(brief, brief.factReview.confirmedAt);
    return JSON.stringify(current) === JSON.stringify(brief.factReview);
}
//# sourceMappingURL=story.js.map